import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cachePlan, getCachedPlan } from '@/utils/offlineCache';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dumbbell, Clock, Flame, ChevronDown, ChevronUp, ChevronRight, Info, Loader2, Play, BookOpen, Heart, Zap, Activity, X, CheckCircle, Check, Lock, Crown, Plus, Minus, Trash2, Crosshair, Sparkles, RefreshCw, Repeat, Brain, MoreVertical, BatteryCharging, Footprints, Eye, Star, Unlock } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ExerciseDetailModal from '@/components/ExerciseDetailModal';
import { useSlowConnection } from '@/components/SmartVideo';
import ExerciseActionMenu from '@/components/ExerciseActionMenu';
import { ExerciseFlagButton } from '@/components/ExerciseFlagButton';
import { CardioPrescriptionDetails } from '@/components/CardioPrescriptionDetails';
import { CardioIntervalToggle } from '@/components/CardioIntervalToggle';
import { ProgressionBadge } from '@/components/ProgressionBadge';
import { ExercisePicker } from '@/components/ExercisePicker';
import WelcomePopup from '@/components/WelcomePopup';
import { ProgramPicker } from '@/components/ProgramPicker';
import { ProgramPhaseBar } from '@/components/ProgramPhaseBar';
import { RecoveryTiles, RecoveryChips, useRecoveryLog, currentWeekDates } from '@/components/RecoveryTiles';
import { BeginSessionLabel } from '@/components/BeginSessionLabel';
import { useEngine } from '@/contexts/EngineContext';
import { getDayFocusMatch, shouldSuppressHighlighting, FOCUS_AREA_OPTIONS } from '@/utils/focusAreaUtils';
import { getSessionSubtitle, formatSessionLabel } from '@/utils/formatSessionType';

/** Exercise thumbnail — shows still image, or video first frame via poster trick */
function ExerciseThumb({ img, videoUrl, onClick, testId }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // For video-only exercises, use video element with time fragment to force frame load
  const hasVideo = !img && videoUrl && !error;
  const videoSrc = hasVideo ? `${videoUrl}#t=0.1` : '';

  return (
    <button
      onClick={onClick}
      className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 active:scale-95 transition-transform border-2 border-accent/20 bg-[#141414]"
      data-testid={testId}
    >
      {img ? (
        <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" width="64" height="64" style={{ filter: 'brightness(0.9) contrast(1.1)' }} />
      ) : hasVideo ? (
        <video
          src={videoSrc}
          className={`w-full h-full object-cover ${loaded ? '' : 'opacity-0'}`}
          preload="metadata"
          muted
          playsInline
          onLoadedData={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      ) : (
        <img src="/bodymax-thumb.jpg" alt="" className="w-full h-full object-cover opacity-50" />
      )}
    </button>
  );
}


const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];const FULL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const CARDIO_TYPES = [
  { id: 'steady', label: 'Steady State', desc: 'Consistent pace, great for endurance' },
  { id: 'interval', label: 'Interval Training', desc: 'Alternating high and low intensity' },
  { id: 'recovery', label: 'Recovery', desc: 'Light pace to aid muscle recovery' },
];

import { CARDIO_IMAGES, getCardioImage } from "@/utils/cardioImages";
import { CardioMedia } from "@/components/CardioMedia";
import { CardioDurationPill } from "@/components/CardioDurationPill";
import { CardioTypeCards } from "@/components/CardioTypeCards";
import { DayCardHero } from "@/components/DayCardHero";
import { DayStatTile } from "@/components/DayStatTile";
import { CardioRoundsStepper } from "@/components/CardioRoundsStepper";
import { CardioMixCard } from "@/components/CardioMixCard";
import { CardioRiskSheet } from "@/components/CardioRiskSheet";
import { AutoMixToggle } from "@/components/AutoMixToggle";
import { formatCardioShort } from "@/utils/cardioLabel";
import { MuscleSetsBoxes } from "@/components/MuscleSetsBoxes";
import { CalibrationBadge } from "@/components/CalibrationBadge";

// Focus tile artwork: a pool per area so each day of the week shows a different one.
const FOCUS_TILE_IMAGES = {
  glutes_booty: ['/focus-glutes-1.webp', '/focus-glutes-2.webp'],
  legs_thighs: ['/focus-legs-1.webp', '/focus-legs-2.webp', '/tile-focus.webp'],
  core_abs: ['/focus-core-1.webp', '/focus-core-2.webp'],
  arms_shoulders: ['/focus-arms-1.webp'],
  chest_push: ['/focus-chest-1.webp'],
  back_posture: ['/focus-back-1.webp'],
};
const FOCUS_TILE_FALLBACK = ['/focus-generic-1.webp', '/tile-exercises.webp'];

const focusTileImage = (areaId, dayNumber) => {
  const pool = FOCUS_TILE_IMAGES[areaId] || FOCUS_TILE_FALLBACK;
  return pool[(dayNumber + (areaId ? areaId.length : 0)) % pool.length];
};

// Goal → the word shown in the day card tag row.
const GOAL_WORDS = {
  muscle_gain: 'Build', build_muscle: 'Build', strength: 'Strength', get_stronger: 'Strength',
  fat_loss: 'Lean', lose_weight: 'Lean', tone: 'Tone', tone_up: 'Tone',
  endurance: 'Endurance', general_fitness: 'Fitness', recomp: 'Recomp', maintain: 'Maintain',
};


/** Stat pill — icon circle + divider + label/value (CSS only, no images) */
function StatBadge({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="inline-flex items-center gap-2 py-2.5 pl-2 pr-3.5 rounded-[14px] bg-[#0B0B0B] border-2 border-accent/75 shrink-0"
      style={{ boxShadow: 'inset 0 1px 0 rgba(var(--accent-rgb),0.08)' }}
      data-testid={testId}
    >
      <span className="w-7 h-7 rounded-full border-2 border-accent/80 flex items-center justify-center shrink-0">
        <Icon size={13} className="text-accent" strokeWidth={2.5} />
      </span>
      <span className="w-[2px] h-5 bg-accent/35 rounded-full shrink-0" />
      <span className="flex flex-col justify-center py-0.5">
        <span className="font-heading font-bold text-[8px] leading-[1.2] uppercase tracking-[0.12em] text-white/85">{label}</span>
        <span className="font-heading font-black text-[12px] leading-[1.2] text-accent whitespace-nowrap">{value}</span>
      </span>
    </div>
  );
}


/**
 * Estimate total session time (minutes) from a workout day's structure.
 * Per exercise: sets × 30s work + (sets-1) × rest + 30s setup/transition
 * Plus warmup, cooldown, cardio durations.
 */
function estimateSessionTime(day) {
  if (!day) return 0;
  let total = 0;

  // Warmup block
  const warmups = day.warmup || [];
  for (const w of warmups) {
    const dur = parseDuration(w.duration);
    total += dur > 0 ? dur : 2;
  }

  // Main exercises
  const main = day.main || day.exercises || [];
  for (const ex of main) {
    const sets = ex.sets || 3;
    const restSec = parseRestSeconds(ex.rest);
    const work = sets * 0.5;                         // 30s work per set
    const rest = Math.max(sets - 1, 0) * (restSec / 60); // rest BETWEEN sets only
    total += work + rest + 0.5;                      // +30s setup/transition
  }

  // Cardio block
  if (day.cardio_block?.duration_minutes) {
    total += day.cardio_block.duration_minutes;
  }

  // Cardio warmup
  if (day.cardio_warmup) {
    const items = Array.isArray(day.cardio_warmup) ? day.cardio_warmup : [day.cardio_warmup];
    for (const cw of items) {
      const dur = parseDuration(cw.duration);
      total += dur > 0 ? dur : 5;
    }
  }

  // Cooldown block
  const cooldowns = day.cooldown || [];
  for (const c of cooldowns) {
    const dur = parseDuration(c.duration);
    total += dur > 0 ? dur : 1;
  }

  return Math.round(total);
}

function parseRestSeconds(rest) {
  if (!rest) return 60;
  const str = String(rest).toLowerCase();
  // Handle "60-90s", "90s", "120", "60-90"
  const match = str.match(/(\d+)/);
  if (match) {
    // If range like "60-90s", take average
    const parts = str.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (parts) return (parseInt(parts[1]) + parseInt(parts[2])) / 2;
    return parseInt(match[1]);
  }
  return 60;
}

function parseDuration(dur) {
  if (!dur) return 0;
  const str = String(dur).toLowerCase();
  // Check if duration is in seconds (e.g., "30-60 sec", "30s")
  if (str.includes('sec') || str.includes(' s') || str.endsWith('s')) {
    const match = str.match(/(\d+)/);
    if (match) {
      // If range like "30-60 sec", take average
      const parts = str.match(/(\d+)\s*[-–]\s*(\d+)/);
      if (parts) return (parseInt(parts[1]) + parseInt(parts[2])) / 2 / 60;
      return parseInt(match[1]) / 60; // Convert seconds to minutes
    }
    return 0;
  }
  // Assume minutes
  const match = str.match(/(\d+)/);
  return match ? parseInt(match[1]) : 0;
}

export default function Workout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user: authUser, updateUser } = useAuth();

  // Fire-and-forget: a failed analytics write must never block the toggle.
  const trackViewedExercises = (dayIndex) => {
    try {
      api.post('/analytics/interaction', { type: 'viewed_exercises', day_index: dayIndex })
        .catch(() => {});
    } catch { /* ignore */ }
  };
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSlowConnection = useSlowConnection();

  // One-time welcome popup — first Workout page visit after program is built.
  // Gated by `welcome_popup_seen` on the user doc so it never fires again, on any
  // device or after a reinstall. The flag is persisted as soon as the popup is SHOWN,
  // not on dismiss: users who closed the app without tapping X used to see it again.
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeMarked = useRef(false);
  useEffect(() => {
    if (!authUser) return;
    if (authUser.welcome_popup_seen) return;
    if (!authUser.onboarding_complete) return;
    if (!workout) return; // wait for the active plan to be resolved
    setShowWelcome(true);
    if (welcomeMarked.current) return;
    welcomeMarked.current = true;
    api.post('/user/welcome-popup-seen').catch(() => {});
  }, [authUser, workout, api]);

  // Warm the week's exercise thumbnails as soon as the plan lands, so expanding any
  // day paints instantly on the first visit too (~200KB of same-origin 320px JPEGs,
  // which the service worker then serves cache-first).
  useEffect(() => {
    if (!workout?.days) return;
    const urls = new Set();
    workout.days.forEach(day => {
      ['main', 'warmup', 'cooldown'].forEach(block => {
        (day?.[block] || []).forEach(ex => {
          const thumb = ex?.media_urls?.thumbnail;
          if (thumb) urls.add(thumb);
        });
      });
    });
    const timer = setTimeout(() => {
      urls.forEach(url => { const img = new Image(); img.decoding = 'async'; img.src = url; });
    }, 300);
    return () => clearTimeout(timer);
  }, [workout]);

  const dismissWelcomePopup = useCallback(async () => {
    setShowWelcome(false);
    // Reflect the flag locally so a re-render doesn't reopen the popup.
    updateUser({ welcome_popup_seen: true });
  }, [updateUser]);
  const [expandedDay, setExpandedDay] = useState(null);
  const [localTimeOverrides, setLocalTimeOverrides] = useState({});
  const [cardioFirstDays, setCardioFirstDaysRaw] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('bodymax_cardio_first') || '{}');
    } catch { return {}; }
  });
  
  const setCardioFirstDays = (updater) => {
    setCardioFirstDaysRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem('bodymax_cardio_first', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [cooldownExpanded, setCooldownExpanded] = useState({});
  const [replaceCardioDayIdx, setReplaceCardioDayIdx] = useState(null);
  const [removeCardioConfirm, setRemoveCardioConfirm] = useState(false);
  const [cardioSessionType, setCardioSessionType] = useState('steady');
  const [cardioDuration, setCardioDuration] = useState(15);
  const [currentCardioModalityId, setCurrentCardioModalityId] = useState(null);
  // Rounds-segment swap mode: { dayIdx, segmentIndex, total, minutes }
  const [roundsSwap, setRoundsSwap] = useState(null);
  const [cardioFavourites, setCardioFavourites] = useState([]);
  // Advisory flow: { mod } — the user may unlock a flagged movement
  const [autoMix, setAutoMix] = useState(true);
  const [riskPrompt, setRiskPrompt] = useState(null);
  const [acceptingRisk, setAcceptingRisk] = useState(false);
  const cardioSheetRef = useRef(null);

  // The session-type row sits at the top of the sheet — always open there.
  useEffect(() => {
    if (replaceCardioDayIdx !== null && cardioSheetRef.current) {
      cardioSheetRef.current.scrollTop = 0;
    }
  }, [replaceCardioDayIdx]);

  const CARDIO_TYPES = [
    { id: 'steady', label: 'Steady State', desc: 'Consistent pace, great for endurance' },
    { id: 'interval', label: 'Interval Training', desc: 'Alternating high and low intensity' },
    { id: 'recovery', label: 'Recovery', desc: 'Light pace to aid muscle recovery' },
  ];
  const [quickSwapDayEx, setQuickSwapDayEx] = useState(null);
  const [quickSwapOptions, setQuickSwapOptions] = useState([]);
  const [quickSwapLoading, setQuickSwapLoading] = useState(false);
  const [showQuickSwapSheet, setShowQuickSwapSheet] = useState(false);
  const [cardioModalities, setCardioModalities] = useState([]);
  const [loadingCardioMods, setLoadingCardioMods] = useState(false);
  const [timePopupDay, setTimePopupDay] = useState(null);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [demoDayInfo, setDemoDayInfo] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedCardioWarmup, setSelectedCardioWarmup] = useState(null);
  const [completedDays, setCompletedDays] = useState(new Set()); // Track completed day numbers
  const defaultSessionLength = authUser?.profile?.session_length || 45;
  const [regenerating, setRegenerating] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const recoveryLog = useRecoveryLog();
  // Day cards are Monday-first, so each card logs against its own calendar date.
  const weekDates = useMemo(() => currentWeekDates(), []);
  // location.key of the navigation whose plan has come back from the server.
  const [serverPlanKey, setServerPlanKey] = useState(null);
  const engine = useEngine();

  const loadQuickSwapForWeekly = async (exercise, dayIndex, exerciseIndex) => {
    setQuickSwapLoading(true);
    setShowQuickSwapSheet(true);
    try {
      const res = await api.post('/workouts/quick-swap-options', {
        workout_plan_id: workout?.id,
        day_index: dayIndex,
        exercise_index: exerciseIndex,
      });
      setQuickSwapOptions(res.data.options || []);
    } catch (err) {
      console.error('Quick swap failed:', err);
      toast.error('Could not load swap options');
      setShowQuickSwapSheet(false);
    } finally {
      setQuickSwapLoading(false);
    }
  };

  const confirmQuickSwapWeekly = async (option) => {
    if (!quickSwapDayEx || !workout) return;
    try {
      await api.post('/workouts/quick-swap-confirm', {
        workout_plan_id: workout.id,
        day_index: quickSwapDayEx.dayIndex,
        exercise_index: quickSwapDayEx.exerciseIndex,
        selected_exercise_name: option.name,
      });
      
      setShowQuickSwapSheet(false);
      setQuickSwapDayEx(null);
      fetchWorkout();
    } catch {
      toast.error('Swap failed');
    }
  };

  const openCardioReplace = async (dayIdx) => {
    // Pre-populate session type + duration from existing block if present
    const existing = workout?.days?.[dayIdx]?.cardio_block;
    setCardioSessionType(existing?.session_type || 'steady');
    setCardioDuration(existing?.duration_minutes || 15);
    setCurrentCardioModalityId(existing?.modality_id || null);
    setRemoveCardioConfirm(false);
    setReplaceCardioDayIdx(dayIdx);
    setLoadingCardioMods(true);
    try {
      const res = await api.get('/cardio/modalities');
      setCardioModalities(res.data.modalities || []);
    } catch (err) {
      toast.error('Failed to load cardio options');
    } finally {
      setLoadingCardioMods(false);
    }
  };

  const openRoundsSegmentSwap = async (dayIdx, segmentIndex) => {
    const block = workout?.days?.[dayIdx]?.cardio_block;
    const segments = block?.rounds_segments || [];
    const segment = segments[segmentIndex];
    if (!segment) return;
    setRoundsSwap({
      dayIdx,
      segmentIndex,
      total: segments.length,
      minutes: segment.rounds_protocol?.total_duration_minutes || segment.target_minutes,
    });
    setCurrentCardioModalityId(segment.modality_id);
    setRemoveCardioConfirm(false);
    setReplaceCardioDayIdx(dayIdx);
    setLoadingCardioMods(true);
    try {
      const res = await api.get('/cardio/modalities?rounds_only=true');
      setCardioModalities(res.data.modalities || []);
      setCardioFavourites(res.data.favourites || []);
    } catch {
      toast.error('Failed to load rounds movements');
    } finally {
      setLoadingCardioMods(false);
    }
  };

  const closeCardioSheet = () => {
    setReplaceCardioDayIdx(null);
    setRemoveCardioConfirm(false);
    setRoundsSwap(null);
  };

  const toggleCardioFavourite = async (modalityId) => {
    const isFav = cardioFavourites.includes(modalityId);
    setCardioFavourites((prev) => (isFav ? prev.filter((m) => m !== modalityId) : [...prev, modalityId]));
    try {
      await api.post('/cardio/favourites', { modality_id: modalityId, favourite: !isFav });
    } catch {
      setCardioFavourites((prev) => (isFav ? [...prev, modalityId] : prev.filter((m) => m !== modalityId)));
      toast.error('Could not save favourite');
    }
  };

  const handleRoundsSegmentSwap = async (modality, acceptRisk = false) => {
    if (!roundsSwap || !workout) return;
    const { dayIdx, segmentIndex } = roundsSwap;
    try {
      const res = await api.post(`/workouts/${workout.id}/cardio-rounds-mix`, {
        day_number: workout.days[dayIdx]?.day_number,
        segment_index: segmentIndex,
        modality_id: modality.id,
        modality_name: modality.name,
        accept_risk: acceptRisk,
      });
      if (res.data?.cardio_block) applyCardioBlockUpdate(dayIdx, res.data.cardio_block);
      closeCardioSheet();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.requires_acknowledgement) {
        setRiskPrompt({ mod: modality, advisory: detail.advisory });
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Could not swap that movement');
    }
  };

  // A flagged movement opens the coach's advice; the user can proceed anyway.
  const pickCardioModality = (mod) => {
    if (mod.eligible === false) {
      if (mod.override_allowed && mod.advisory) {
        setRiskPrompt({ mod, advisory: mod.advisory });
      } else {
        toast.error(mod.locked_label || 'Not available');
      }
      return;
    }
    if (roundsSwap) handleRoundsSegmentSwap(mod);
    else handleCardioReplace(replaceCardioDayIdx, mod);
  };

  const acceptCardioRisk = async () => {
    const mod = riskPrompt?.mod;
    if (!mod) return;
    setAcceptingRisk(true);
    try {
      const res = await api.post('/cardio/acknowledge-risk', { modality_id: mod.id, accept: true });
      const overrides = res.data?.overrides || [];
      setCardioModalities((prev) => prev.map((m) => (
        overrides.includes(m.id) ? { ...m, eligible: true, acknowledged: true } : m
      )));
      setRiskPrompt(null);
      if (roundsSwap) await handleRoundsSegmentSwap(mod, true);
      else await handleCardioReplace(replaceCardioDayIdx, mod, true);
      toast.success(`${mod.name} unlocked — your level's dose applies`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not unlock that movement');
    } finally {
      setAcceptingRisk(false);
    }
  };

  const handleKeepSingle = async (dayIdx) => {
    if (!workout) return;
    try {
      const res = await api.post(`/workouts/${workout.id}/cardio-rounds-mix`, {
        day_number: workout.days[dayIdx]?.day_number,
        keep_single: true,
      });
      if (res.data?.cardio_block) applyCardioBlockUpdate(dayIdx, res.data.cardio_block);
      toast.success('One movement for the whole block');
    } catch {
      toast.error('Could not update this block');
    }
  };

  const handleCardioReplace = async (dayIdx, modality, acceptRisk = false) => {
    if (!workout) return;
    try {
      const dayNumber = workout.days[dayIdx]?.day_number;
      const res = await api.post(`/workouts/${workout.id}/replace-cardio`, {
        day_number: dayNumber,
        modality_id: modality.id,
        modality_name: modality.name,
        session_type: cardioSessionType,
        duration_minutes: cardioDuration,
        accept_risk: acceptRisk,
        auto_mix: autoMix,
      });
      const savedType = res?.data?.session_type || cardioSessionType;
      if (res?.data?.note) toast.info(res.data.note);
      // Update local state — handles both replace and add
      setWorkout(prev => {
        if (!prev) return prev;
        const newDays = [...prev.days];
        const existing = newDays[dayIdx].cardio_block;
        newDays[dayIdx] = {
          ...newDays[dayIdx],
          cardio_block: {
            ...(existing || { completed: false, is_finisher: true }),
            modality_id: modality.id,
            modality_name: modality.name,
            image_url: modality.image_url || null,
            session_type: savedType,
            duration_minutes: cardioDuration,
          }
        };
        return { ...prev, days: newDays };
      });
      setReplaceCardioDayIdx(null);
      setRemoveCardioConfirm(false);
      setRoundsSwap(null);
      fetchWorkout();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.requires_acknowledgement) {
        setRiskPrompt({ mod: modality, advisory: detail.advisory });
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Failed to update cardio');
    }
  };

  const applyCardioBlockUpdate = (dayIdx, block) => {
    setWorkout(prev => {
      if (!prev) return prev;
      const newDays = [...prev.days];
      newDays[dayIdx] = { ...newDays[dayIdx], cardio_block: block };
      return { ...prev, days: newDays };
    });
  };

  const handleCardioRemove = async (dayIdx) => {
    if (!workout) return;
    try {
      const dayNumber = workout.days[dayIdx]?.day_number;
      await api.post(`/workouts/${workout.id}/remove-cardio`, { day_number: dayNumber });
      setWorkout(prev => {
        if (!prev) return prev;
        const newDays = [...prev.days];
        newDays[dayIdx] = { ...newDays[dayIdx], cardio_block: null };
        return { ...prev, days: newDays };
      });
      setReplaceCardioDayIdx(null);
      setRemoveCardioConfirm(false);
      toast.success('Cardio finisher removed');
    } catch (err) {
      toast.error('Failed to remove cardio');
    }
  };
  
  // Signal destination ready once the plan is actually painted (for engine overlay transition).
  // useLayoutEffect fires synchronously after DOM mutation; the requestAnimationFrame callback
  // fires on the first browser task after the next paint.  Because microtasks (including the
  // `await waitForDestinationReady()` continuation in EngineOverlay) always drain before tasks,
  // destinationReadyResolveRef is guaranteed to be set before signalDestinationReady() fires —
  // eliminating the race where the signal was lost when workout loaded from the localStorage
  // cache before waitForDestinationReady had a chance to install its resolve ref.
  // The engine.visible guard keeps this a no-op on ordinary /workout navigations.
  useLayoutEffect(() => {
    if (!workout || !engine.visible) return;
    // After a program switch we must wait for THIS navigation's server plan, not the
    // stale one still on screen, before letting the overlay fade.
    if (serverPlanKey !== location.key) return;
    const rafId = requestAnimationFrame(() => {
      console.log('[WORKOUT] destination ready — plan painted, releasing engine overlay');
      engine.signalDestinationReady();
    });
    return () => cancelAnimationFrame(rafId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workout, engine.visible, serverPlanKey, location.key]);

  // Prefetch the first exercise video of today's (or next) training day.
  // Plain fetch() lets the service worker cache the full file, so the
  // session opener plays instantly when the workout starts.
  const prefetchedOpenerRef = useRef(false);
  useEffect(() => {
    if (!workout?.days || prefetchedOpenerRef.current) return;
    const jsDay = new Date().getDay();
    const todayIdx = jsDay === 0 ? 6 : jsDay - 1;
    for (let offset = 0; offset < 7; offset++) {
      const day = workout.days[(todayIdx + offset) % 7];
      if (day && day.main?.length > 0 && day.type !== 'Rest' && day.type !== 'Recovery') {
        const firstEx = day.main[0];
        const vid = firstEx?.video_url || firstEx?.media_urls?.video;
        if (vid) {
          prefetchedOpenerRef.current = true;
          fetch(vid).catch(() => {});
        }
        break;
      }
    }
  }, [workout]);
  
  // Paywall state — default LOCKED until API confirms access
  const [accessStatus, setAccessStatus] = useState({
    can_start_workouts: true,
    subscription_required: false,
    free_session_used: false,
    is_dev: false,
    has_subscription: true
  });
  const [pickerState, setPickerState] = useState({ open: false, dayIndex: null, dayType: '', exercises: [], mode: 'add', replaceIndex: null });
  const [userFocusAreas, setUserFocusAreas] = useState([]);
  const [userGoal, setUserGoal] = useState(null);
  // Day tabs highlight today by default (Mon = 1 … Sun = 7).
  const [activeDayTab, setActiveDayTab] = useState(() => ((new Date().getDay() + 6) % 7) + 1);
  const [userFocusMode, setUserFocusMode] = useState('skipped');

  // Locked days bounce the user back to the demo day's Start button (UI only).
  const [bounceDemo, setBounceDemo] = useState(false);
  const demoAvailable = !accessStatus.has_subscription && !accessStatus.demo_consumed;
  const bounceToDemo = useCallback(() => {
    const el = document.querySelector('[data-testid^="demo-workout-day-"]');
    if (!el) return;
    setExpandedDay(null);
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setBounceDemo(true);
    window.setTimeout(() => setBounceDemo(false), 1900);
  }, []);

  // Arriving from the home card (?focus=demo) lands on the demo Start button.
  useEffect(() => {
    if (!workout || !demoAvailable) return;
    if (!new URLSearchParams(location.search).get('focus')) return;
    const t = window.setTimeout(bounceToDemo, 650);
    return () => window.clearTimeout(t);
  }, [workout, demoAvailable, location.search, bounceToDemo]);

  // Fetch workout every time this page is navigated to
  // location.key changes on every navigation (even same path)
  useEffect(() => { fetchWorkout(); fetchAccessStatus(); fetchUserFocusAreas(); }, [id, location.key]);
  
  // Also refetch when page becomes visible (tab switch back)
  useEffect(() => {
    const handleVisibility = () => { if (!document.hidden) fetchWorkout(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);
  
  const fetchAccessStatus = async () => {
    try {
      const res = await api.get('/workout/access-status');
      setAccessStatus(res.data);
    } catch (err) {
      console.error('Could not fetch access status:', err);
    }
  };


  const fetchUserFocusAreas = async () => {
    try {
      const res = await api.get('/user/focus-areas');
      setUserFocusAreas(res.data.focus_areas || []);
      setUserGoal(res.data.goal || null);
      setUserFocusMode(res.data.focus_area_mode || (res.data.focus_areas?.length ? 'specific' : 'skipped'));
    } catch (err) {
      // Silent fail — highlighting just won't show
    }
  };

  const fetchWorkout = async () => {
    const navKey = location.key;
    let fetchedPlan = null;
    try {
      // Show cached data instantly — but DON'T use it for Start button
      // The API response will override with the real active plan
      const cached = localStorage.getItem('workout_plan_cache');
      if (cached && loading) {
        try {
          const c = JSON.parse(cached);
          if (c.workout) { setWorkout(c.workout); setLoading(false); }
        } catch {}
      }
      // Also check offline cache
      if (!cached) {
        const offlinePlan = getCachedPlan();
        if (offlinePlan && loading) { setWorkout(offlinePlan); setLoading(false); }
      }
      
      if (id) {
        const res = await api.get('/workouts');
        const found = res.data.workouts?.find(w => w.id === id);
        setWorkout(found || null);
        fetchedPlan = found || null;
        
        // Fetch completed sessions for this plan (same as no-id path)
        if (found) {
          try {
            const sessionsRes = await api.get('/workout-sessions?status=completed&limit=50');
            const sessions = sessionsRes.data.sessions || [];
            
            // Get start of current week (Monday)
            const now = new Date();
            const dayOfWeek = now.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const weekStart = new Date(now);
            weekStart.setDate(now.getDate() + mondayOffset);
            weekStart.setHours(0, 0, 0, 0);
            
            const completed = new Set();
            sessions.forEach(session => {
              // A skipped demo RESETS — it must not mark the day as done.
              if (session.demo_skipped) return;
              if (session.status === 'completed' && session.day_number) {
                if (session.workout_plan_id !== found.id) return;
                const completedAt = session.completed_at || session.ended_at || session.started_at;
                if (completedAt) {
                  // Safari-safe date parsing (append Z if no timezone)
                  const dateStr = completedAt.includes('Z') || completedAt.includes('+') ? completedAt : completedAt + 'Z';
                  const sessionDate = new Date(dateStr);
                  if (!isNaN(sessionDate.getTime()) && sessionDate >= weekStart) {
                    completed.add(session.day_number);
                  }
                }
              }
            });
            setCompletedDays(completed);
          } catch (e) {
            console.log('Could not fetch completed sessions:', e);
          }
        }
      } else {
        const res = await api.get('/workouts/active');
        const activeWorkout = res.data.workout;
        setWorkout(activeWorkout);
        fetchedPlan = activeWorkout;
        
        // Fetch completed sessions — only count sessions from CURRENT active plan
        try {
          const sessionsRes = await api.get('/workout-sessions?status=completed&limit=50');
          const sessions = sessionsRes.data.sessions || [];
          const activePlanId = activeWorkout?.id;
          
          const now = new Date();
          const dayOfWeek = now.getDay();
          const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() + mondayOffset);
          weekStart.setHours(0, 0, 0, 0);
          
          const completed = new Set();
          sessions.forEach(session => {
            // A skipped demo RESETS — it must not mark the day as done.
            if (session.demo_skipped) return;
            if (session.status === 'completed' && session.day_number) {
              if (activePlanId && session.workout_plan_id !== activePlanId) return;
              const completedAt = session.completed_at || session.ended_at || session.started_at;
              if (completedAt) {
                const dateStr = completedAt.includes('Z') || completedAt.includes('+') ? completedAt : completedAt + 'Z';
                const sessionDate = new Date(dateStr);
                if (!isNaN(sessionDate.getTime()) && sessionDate >= weekStart) {
                  completed.add(session.day_number);
                }
              }
            }
          });
          setCompletedDays(completed);
        } catch (e) {
          console.log('Could not fetch completed sessions:', e);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      // Release the engine overlay even if the fetch failed — it must never hang on the 5s timeout.
      setServerPlanKey(navKey);
      // Cache the plan we just fetched, never the stale one from before this call.
      if (fetchedPlan) {
        try { localStorage.setItem('workout_plan_cache', JSON.stringify({ workout: fetchedPlan })); } catch {}
        cachePlan(fetchedPlan);
      }
    }
  };

  const handleRegeneratePlan = useCallback(() => {
    if (regenerating || engine.visible) return;
    setRegenerating(true);
    engine.trigger({
      subtitle: 'Rebuilding your training program',
      mode: 'week',
      week: workout?.week || 1,
      minutes: (workout?.days || []).slice(0, 7).map(d => estimateSessionTime(d)),
      generationFn: async () => {
        const res = await api.post('/workouts/generate');
        return res.data;
      },
      onSuccess: (data) => {
        setWorkout(data);
      },
      onError: () => {
        toast.error('Failed to generate workout plan');
      },
      onDismiss: () => {
        setRegenerating(false);
      },
    });
  }, [regenerating, engine, api]);


  // Exercise Picker handlers
  const openPicker = useCallback((dayIndex, dayType, mainExercises) => {
    setPickerState({ open: true, dayIndex, dayType, exercises: mainExercises || [], mode: 'add', replaceIndex: null });
  }, []);

  const openPickerForReplace = useCallback((dayIndex, exerciseIndex) => {
    const day = workout?.days?.[dayIndex];
    const mainExercises = day?.main || day?.exercises || [];
    setPickerState({ open: true, dayIndex, dayType: day?.type || '', exercises: mainExercises, mode: 'replace', replaceIndex: exerciseIndex });
  }, [workout]);

  const closePicker = useCallback(() => {
    setPickerState(s => ({ ...s, open: false }));
  }, []);

  const handleExerciseAdded = useCallback((data) => {
    if (data.updated_main) {
      setWorkout(prev => {
        if (!prev) return prev;
        const newDays = [...prev.days];
        const mainKey = newDays[pickerState.dayIndex]?.main ? 'main' : 'exercises';
        newDays[pickerState.dayIndex] = { ...newDays[pickerState.dayIndex], [mainKey]: data.updated_main };
        return { ...prev, days: newDays };
      });
    }
  }, [pickerState.dayIndex]);

  const handleExerciseReplaced = useCallback((data) => {
    if (data.newExercise && pickerState.dayIndex !== null) {
      setWorkout(prev => {
        if (!prev) return prev;
        const newDays = [...prev.days];
        const mainKey = newDays[pickerState.dayIndex]?.main ? 'main' : 'exercises';
        const main = [...(newDays[pickerState.dayIndex]?.[mainKey] || [])];
        if (data.exerciseIndex !== undefined && data.exerciseIndex < main.length) {
          main[data.exerciseIndex] = data.newExercise;
        }
        newDays[pickerState.dayIndex] = { ...newDays[pickerState.dayIndex], [mainKey]: main };
        return { ...prev, days: newDays };
      });
    }
  }, [pickerState.dayIndex]);

  const handleRemoveExercise = useCallback(async (dayIndex, exerciseIndex, exerciseName) => {
    try {
      const res = await api.delete('/workouts/remove-exercise-from-day', {
        data: {
          workout_plan_id: workout.id,
          day_index: dayIndex,
          exercise_index: exerciseIndex,
        }
      });
      if (res.data.success) {
        setWorkout(prev => {
          if (!prev) return prev;
          const newDays = [...prev.days];
          const mainKey = newDays[dayIndex]?.main ? 'main' : 'exercises';
          newDays[dayIndex] = { ...newDays[dayIndex], [mainKey]: res.data.updated_main };
          return { ...prev, days: newDays };
        });
        const ti = res.data.time_impact;
        toast.success(`Removed ${exerciseName}${ti?.removed_minutes ? ` (-${ti.removed_minutes}min)` : ''}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove exercise');
    }
  }, [api, workout?.id]);

  if (loading && !workout) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl md:max-w-4xl mx-auto px-4 md:px-8 pt-4 space-y-4">
          <div className="h-44 rounded-3xl bg-[#1A1A1A] animate-pulse" />
          <div className="h-8 w-48 rounded-lg bg-[#1A1A1A] animate-pulse" />
          {[1,2,3,4,5,6,7].map(i => (
            <div key={i} className="h-32 rounded-2xl bg-[#1A1A1A] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="min-h-screen bg-background">
        <main className="max-w-3xl md:max-w-4xl mx-auto px-4 md:px-8 sm:px-5 py-8">
          <div className="flex flex-col items-center justify-center py-12 space-y-4 mb-8">
            <Dumbbell size={48} className="text-muted-foreground" />
            <p className="text-muted-foreground text-center">No workout plan found.</p>
            <Button
              onClick={handleRegeneratePlan}
              disabled={regenerating}
              className="rounded-full gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="generate-plan-btn"
            >
              {regenerating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              {regenerating ? 'Generating Your Plan...' : 'Generate My Workout Plan'}
            </Button>
          </div>
          
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-page-bg" data-testid="workout-page">
      <WelcomePopup open={showWelcome} onDismiss={dismissWelcomePopup} />
      <ProgramPicker api={api} open={pickerOpen} onOpenChange={setPickerOpen} />
      {recoveryLog.sheet}
      <style>{`
        @keyframes demoPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(var(--accent-rgb),0.3), 0 0 0 0 rgba(var(--accent-rgb),0.4); transform: scale(1); }
          50% { box-shadow: 0 0 20px rgba(var(--accent-rgb),0.5), 0 0 0 8px rgba(var(--accent-rgb),0); transform: scale(1.05); }
        }
        @keyframes demoPulseBlue {
          0%, 100% { box-shadow: 0 0 10px rgba(10,132,255,0.35), 0 0 0 0 rgba(10,132,255,0.45); transform: scale(1); }
          50% { box-shadow: 0 0 24px rgba(10,132,255,0.55), 0 0 0 8px rgba(10,132,255,0); transform: scale(1.03); }
        }
        @keyframes demoBounce {
          0%, 100% { box-shadow: inset 0 0 0 1.5px rgba(79,169,255,0.9), 0 0 24px rgba(10,132,255,0.55), inset 0 0 26px rgba(10,132,255,0.35); transform: scale(1); }
          40% { box-shadow: inset 0 0 0 2.5px rgba(150,215,255,1), 0 0 44px rgba(10,132,255,0.95), 0 0 0 10px rgba(10,132,255,0.18), inset 0 0 36px rgba(10,132,255,0.6); transform: scale(1.055); }
        }
        .demo-bounce { animation: demoBounce 0.62s ease-in-out 3; }
        @media (prefers-reduced-motion: reduce) {
          .demo-bounce { animation: none; }
        }
        @keyframes exerciseSheen {
          0% { transform: translateX(-150%) skewX(-18deg); }
          55%, 100% { transform: translateX(320%) skewX(-18deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes exerciseSheen {
            0%, 100% { transform: translateX(-150%) skewX(-18deg); }
          }
        }
      `}</style>
      <main className="max-w-3xl md:max-w-4xl mx-auto px-4 md:px-8 sm:px-5 pt-4 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">

        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h1 className="font-heading font-black uppercase tracking-tight leading-[0.92] text-white text-3xl sm:text-4xl" data-testid="workout-title">
                {workout.active_program_label || workout.program_name || 'Your Program'}
              </h1>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {workout.generation_method === 'custom_builder' ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/custom-builder')}
                    className="h-9 rounded-lg gap-1.5 text-xs font-heading font-bold uppercase tracking-wider border-white/15 text-accent"
                    data-testid="edit-custom-btn"
                  >
                    <Dumbbell size={14} /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPickerOpen(true)}
                    className="h-8 rounded-lg gap-1.5 text-xs font-heading uppercase tracking-wider text-[#A3A3A3]"
                    data-testid="program-picker-open"
                    disabled={switchingMode}
                  >
                    <Repeat size={14} /> Switch Program
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/custom-builder')}
                    className="h-9 rounded-lg gap-1.5 text-xs font-heading font-bold uppercase tracking-wider border-white/15 text-accent"
                    data-testid="custom-builder-btn"
                  >
                    <Dumbbell size={14} /> Build Own
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPickerOpen(true)}
                    className="h-8 rounded-lg gap-1.5 text-xs font-heading uppercase tracking-wider text-[#A3A3A3]"
                    data-testid="program-picker-open"
                    disabled={switchingMode}
                  >
                    <Repeat size={14} /> Switch Program
                  </Button>
                </>
              )}
            </div>
          </div>

          {workout.active_program_label && (
            <ProgramPhaseBar
              programName={workout.active_program_label}
              week={workout.program_week || workout.week}
              durationWeeks={workout.program_duration_weeks}
              className="mb-6 sm:max-w-sm"
            />
          )}

          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-10 sm:max-w-sm" data-testid="week-day-blocks">
            {SHORT_DAYS.map((short, idx) => {
              const dayNumber = idx + 1;
              const isDone = completedDays.has(dayNumber);
              const isActive = activeDayTab === dayNumber;
              return (
                <button
                  key={`${short}-${dayNumber}`}
                  type="button"
                  onClick={() => {
                    setActiveDayTab(dayNumber);
                    const el = document.getElementById(`day-card-${dayNumber}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  aria-label={`${FULL_DAYS[idx]}${isDone ? ' completed' : ''}`}
                  aria-pressed={isActive}
                  data-testid={`week-day-block-${dayNumber}`}
                  className={`h-11 rounded-xl border-2 font-heading font-black text-[13px] uppercase tracking-tight transition-all ${
                    isActive
                      ? 'border-accent bg-accent text-accent-foreground'
                      : isDone
                        ? 'border-accent/70 bg-accent/10 text-accent'
                        : 'border-border text-white/40 hover:border-accent/50'
                  }`}
                  style={isActive
                    ? { boxShadow: '0 0 18px rgba(var(--accent-rgb),0.45)' }
                    : isDone
                      ? { boxShadow: '0 0 14px rgba(var(--accent-rgb),0.35), inset 0 0 10px rgba(var(--accent-rgb),0.12)' }
                      : {}}
                >
                  {short}
                  {isActive && <span className="block mx-auto mt-0.5 h-[2px] w-4 rounded-full bg-black/70" />}
                </button>
              );
            })}
          </div>

          {/* Trial/Access Status Banner */}
          {(accessStatus.subscription_status === 'prelaunch_trial' || accessStatus.subscription_status === 'manual_access') && accessStatus.days_left != null && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-[#141414] border border-white/5 flex items-center justify-between" data-testid="trial-banner">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-accent" />
                <span className="text-sm font-medium">
                  {accessStatus.days_left > 0
                    ? `${accessStatus.days_left} day${accessStatus.days_left !== 1 ? 's' : ''} left of ${accessStatus.subscription_status === 'manual_access' ? '' : 'free '}access`
                    : `Less than 24h of ${accessStatus.subscription_status === 'manual_access' ? '' : 'free '}access remaining`
                  }
                </span>
              </div>
            </div>
          )}
          {(accessStatus.subscription_status === 'prelaunch_trial_ended' || accessStatus.subscription_status === 'manual_access_expired') && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-[#141414] border border-amber-500/20 flex items-center gap-2" data-testid="trial-expired-banner">
              <Lock size={16} className="text-amber-500" />
              <span className="text-sm text-amber-500">Your free access has ended.</span>
            </div>
          )}

          <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-6 md:items-start">
            {(() => {
              const suppress = shouldSuppressHighlighting(workout.days, userFocusAreas, userFocusMode);
              const isFreeUser = !accessStatus.has_subscription;
              // Persistent flag — a consumed demo never re-opens (week-scoped
              // completedDays would re-expose day 1 next week).
              const demoConsumed = !!accessStatus.demo_consumed;
              const firstTrainingDayIdx = workout.days?.findIndex(d => d.main?.length > 0 && d.type !== 'Rest' && d.type !== 'Recovery');
              return workout.days?.map((day, i) => {
              const dayKey = day.id || `day-${day.day_number || i}-${day.day_name || 'unknown'}`;
              const focusMatch = !suppress ? getDayFocusMatch(day, userFocusAreas, userFocusMode) : { matches: false, matchedAreas: [] };
              const isFirstTrainingDay = i === firstTrainingDayIdx;
              const isDemoDay = isFreeUser && isFirstTrainingDay && !demoConsumed;
              // Demo spent: day 1 stays viewable (no Begin), everything else locks.
              const isViewOnlyDay = isFreeUser && demoConsumed && isFirstTrainingDay;
              const isLockedDay = isFreeUser && !isDemoDay && !isViewOnlyDay && day.main?.length > 0 && day.type !== 'Rest' && day.type !== 'Recovery';
              // Pre-demo day 1: exercise drop-down stays shut — Begin session is the only path.
              const expandBlocked = isLockedDay || isDemoDay;

              // Locked days (demo still unused) bounce back to the demo Start button.
              const blockedTap = (isLockedDay && demoAvailable) ? bounceToDemo : undefined;

              const isRestDay = day.type === 'Rest' || day.type === 'Recovery' || !!day.is_rest;
              // Program names never show a trailing variant letter ("Full Body Tone A" → "Full Body Tone").
              const splitLabel = (formatSessionLabel(day) || '').replace(/\s+[A-D]$/, '');
              const goalWord = GOAL_WORDS[userGoal];
              // Curated/preset splits often already read like "Full Body Tone" — don't repeat parts.
              const extend = (parts) => {
                const out = [];
                parts.filter(Boolean).forEach(part => {
                  const sofar = out.join(' ').toLowerCase();
                  if (!sofar.includes(String(part).toLowerCase())) out.push(part);
                });
                return out.join(' ');
              };
              const dayTitle = completedDays.has(day.day_number)
                ? 'Workout Completed'
                : isRestDay
                  ? 'Recovery Day'
                  : extend([
                      splitLabel || day.day_name || `Day ${i + 1}`,
                      getSessionSubtitle(day),
                      day.main?.length > 0 ? goalWord : null,
                    ]);
              const dayTags = (isRestDay || completedDays.has(day.day_number)) ? [] : [
                day.main?.length > 0 && 'Strength',
                day.cardio_block && 'Cardio',
                goalWord,
                (focusMatch.matchedAreas || []).length
                  ? (FOCUS_AREA_OPTIONS.find(o => o.id === focusMatch.matchedAreas[0])?.label || null)
                  : null,
              ].filter(Boolean)
                // Never echo a word the title already says (e.g. a Cardio day).
                .filter(tag => !dayTitle.toLowerCase().includes(tag.toLowerCase()))
                .slice(0, 4);

              // Single source for the day's action button — rendered under the EXERCISES
              // toggle when collapsed, or at the end of the exercise list when expanded.
              const dayActionButton = (day.type !== 'Rest' && day.type !== 'Recovery' && (day.main?.length > 0 || day.cardio_block)) ? (
                <div className="flex flex-col items-center pt-4 w-full">
                  {completedDays.has(day.day_number) ? (
                    <Button disabled size="sm" className="rounded-xl gap-1.5 shrink-0 h-11 px-4 font-heading font-bold uppercase tracking-wider text-sm" style={{ background: 'rgba(var(--accent-muted-rgb),0.15)', color: 'var(--accent-muted)', border: '1px solid rgba(var(--accent-muted-rgb),0.40)' }} data-testid={`completed-workout-day-${i + 1}`}>
                      <Check size={15} /> Done
                    </Button>
                  ) : (isLockedDay || isViewOnlyDay) ? (
                    <button onClick={(e) => { e.stopPropagation(); navigate('/subscribe'); }} className="relative block w-1/2 mx-auto active:scale-[0.98] transition-transform rounded-xl" data-testid={`locked-workout-day-${i + 1}`} aria-label="7 days free trial">
                      <img src="/btn-7days-free.webp" alt="7 Days Free Trial" className="w-full h-auto select-none pointer-events-none" draggable={false} />
                      <span aria-hidden="true" className="begin-beam pointer-events-none absolute inset-[1px] rounded-[11px]" />
                    </button>
                  ) : isDemoDay ? (
                    <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDemoDayInfo({ planId: workout.id, dayNumber: day.day_number, cardioFirst: cardioFirstDays[i] }); setShowDemoPopup(true); }}
                      className={`relative block w-full mx-auto aspect-[925/186] rounded-2xl font-heading font-black uppercase tracking-wider text-[22px] min-[360px]:text-2xl text-white flex items-center justify-center active:scale-[0.98] transition-transform ${bounceDemo ? 'demo-bounce' : 'animate-[demoPulseBlue_2s_ease-in-out_infinite]'}`}
                      style={{ background: 'radial-gradient(120% 140% at 50% 0%, #0C3A74 0%, #061427 70%)', boxShadow: 'inset 0 0 0 1.5px rgba(79,169,255,0.9), 0 0 24px rgba(10,132,255,0.55), inset 0 0 26px rgba(10,132,255,0.35)', textShadow: '0 0 12px rgba(124,212,255,0.9)' }}
                      data-testid={`demo-workout-day-${i + 1}`}
                      aria-label="Begin session"
                    >
                      Start Training
                    </button>
                    <p className="mt-2 text-xs font-bold text-white" data-testid={`demo-first-program-hint-${i + 1}`}>
                      View your first program
                    </p>
                    </>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/workout/active/${workout.id}/${day.day_number}${cardioFirstDays[i] ? '?cardioFirst=1' : ''}`); }}
                      className="relative block w-full mx-auto aspect-[925/186] rounded-2xl font-heading font-black uppercase tracking-wider text-[22px] min-[360px]:text-2xl text-white flex items-center justify-center active:scale-[0.98] transition-transform"
                      style={{ background: 'radial-gradient(120% 140% at 50% 0%, #0C3A74 0%, #061427 70%)', boxShadow: 'inset 0 0 0 1.5px rgba(79,169,255,0.9), 0 0 24px rgba(10,132,255,0.55), inset 0 0 26px rgba(10,132,255,0.35)', textShadow: '0 0 12px rgba(124,212,255,0.9)' }}
                      data-testid={`start-workout-day-${i + 1}`}
                      aria-label="Begin session"
                    >
                      Start Training
                    </button>
                  )}
                </div>
              ) : null;

              return (
              <Fragment key={dayKey}>
              <motion.div
                id={`day-card-${day.day_number || i + 1}`}
                className={`rounded-3xl overflow-hidden border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01]`}
                style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 48px rgba(0,0,0,0.55)' }}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <DayCardHero
                  week={workout.week || 1}
                  dayShort={SHORT_DAYS[i]}
                  title={dayTitle}
                  tags={dayTags}
                  done={completedDays.has(day.day_number)}
                  rest={isRestDay}
                  onClick={expandBlocked ? blockedTap : () => setExpandedDay(expandedDay === i ? null : i)}
                  testId={`workout-day-${i + 1}`}
                  titleTestId={`day-plan-type-${i + 1}`}
                />

                {/* Recovery log: same three pillars as a rest day, compact under the session tags */}
                {!isRestDay && (day.main?.length > 0 || day.cardio_block) && (
                  <div className="px-4 pt-1">
                    <RecoveryChips log={recoveryLog} date={weekDates[i]} testId={`training-day-recovery-chips-${i + 1}`} />
                  </div>
                )}

                {/* Calibration banner — shown while the coach is still learning this plan */}
                {!isRestDay && day.main?.some(ex => ex.progression?.action === 'calibration') && (
                  <div className="px-4 pt-3">
                    <CalibrationBadge
                      done={completedDays.has(day.day_number)}
                      testId={`calibration-badge-day-${i + 1}`}
                    />
                  </div>
                )}

                {/* Stats tiles: exercises, session time, cardio, focus */}
                {!isRestDay && (day.main?.length > 0 || day.cardio_block) && (
                  <div className="px-4 pt-3 pb-1 grid grid-cols-2 gap-2.5">
                    {day.main?.length > 0 && (
                      <DayStatTile
                        iconSrc="/icon-stat-exercises.webp"
                        label="Exercises"
                        value={day.main.length}
                        image="/tile-exercises.webp"
                        fill={62}
                        tone={completedDays.has(day.day_number) ? 'done' : 'accent'}
                        onClick={expandBlocked ? blockedTap : () => setExpandedDay(expandedDay === i ? null : i)}
                        testId={`exercise-count-badge-${i}`}
                      />
                    )}
                    {day.main?.length > 0 && (
                      <DayStatTile
                        iconSrc="/icon-stat-time.webp"
                        label="Time"
                        value={`${day.estimated_time ? Math.round(day.estimated_time) : estimateSessionTime(day)}m`}
                        image="/tile-time.webp"
                        fill={74}
                        tone={completedDays.has(day.day_number) ? 'done' : 'accent'}
                        onClick={expandBlocked ? blockedTap : () => { setExpandedDay(i); setTimePopupDay(i); }}
                        testId={`est-time-day-${i}`}
                      />
                    )}
                    <DayStatTile
                      iconSrc="/icon-stat-cardio.webp"
                      label={day.cardio_block?.rounds_protocol
                        ? (day.cardio_block.rounds_protocol.rpe_adjusted ? 'Cardio tuned' : 'Cardio rounds')
                        : 'Cardio'}
                      value={day.cardio_block ? formatCardioShort(day.cardio_block) : 'NONE'}
                      image="/tile-cardio.webp"
                      fill={55}
                      muted={!day.cardio_block}
                      tone={completedDays.has(day.day_number) ? 'done' : 'accent'}
                      onClick={expandBlocked ? blockedTap : () => openCardioReplace(i)}
                      testId={`cardio-minutes-day-${i}`}
                    />
                    <DayStatTile
                      iconSrc="/icon-stat-focus.webp"
                      label="Focus"
                      value={focusMatch.matches
                        ? ((focusMatch.matchedAreas || [])
                            .map(id => (FOCUS_AREA_OPTIONS.find(o => o.id === id)?.label || id))
                            .join(' + ')
                            .toUpperCase() || 'FULL BODY')
                        : 'NONE'}
                      image={focusTileImage(focusMatch.matches ? (focusMatch.matchedAreas || [])[0] : null, day.day_number)}
                      fill={80}
                      muted={!focusMatch.matches}
                      tone={completedDays.has(day.day_number) ? 'done' : 'accent'}
                      testId={`focus-badge-day-${i}`}
                    />
                  </div>
                )}

                {/* Add Cardio lives inside the expanded card only (see add-cardio-expanded-*) */}

                {/* View Day toggle — shown on all days with content; inert on locked days */}
                {(day.main?.length > 0 || day.warmup?.length > 0 || day.cardio_block || day.cooldown?.length > 0) && (
                <div className="px-5 pb-4 pt-5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (expandBlocked) { if (blockedTap) blockedTap(); return; }
                      if (expandedDay !== i) trackViewedExercises(i);
                      setExpandedDay(expandedDay === i ? null : i);
                    }}
                    className="relative w-full flex items-center gap-3 overflow-hidden rounded-2xl border border-white/[0.14] bg-white/[0.04] px-3 py-3 backdrop-blur-md active:scale-[0.97] transition-transform"
                    style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), 0 10px 26px rgba(0,0,0,0.45)' }}
                    data-testid={`view-exercises-toggle-${i}`}
                    aria-label={expandedDay === i ? 'Hide exercises' : 'View exercises'}
                  >
                    <img
                      src="/exercises-pill-photo.webp"
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-y-0 right-0 h-full w-3/5 select-none object-cover opacity-60"
                      style={{ maskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)', WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)' }}
                      draggable={false}
                    />
                    <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/[0.10] bg-black/70">
                      <Dumbbell size={22} className="text-white" />
                    </span>
                    <span className="relative min-w-0 flex-1 text-left">
                      <span className="block font-heading text-[20px] font-black uppercase leading-none tracking-tight text-white" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                        Exercises
                      </span>
                      <span className="mt-1 block truncate text-[12px] text-white/60" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
                        {expandedDay === i ? 'Hide your exercises' : 'View or hide exercises'}
                      </span>
                    </span>
                    <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-black/70">
                      <ChevronRight size={18} className={`text-white transition-transform ${expandedDay === i ? 'rotate-90' : ''}`} />
                    </span>
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <div
                        className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent"
                        style={expandedDay === i ? { opacity: 0 } : { animation: 'exerciseSheen 3.2s ease-in-out infinite', animationDelay: `${i * 0.25}s` }}
                      />
                    </div>
                  </button>
                  {expandedDay !== i && dayActionButton}
                </div>
                )}

                {expandedDay === i && (day.main?.length > 0 || day.cardio_block) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    className="px-4 pb-4 space-y-4"
                  >
                    {/* Session Time — clock button triggers popup */}
                    {(() => {
                      const estTime = day.estimated_time ? Math.round(day.estimated_time) : estimateSessionTime(day);
                      const targetTime = day.session_length_override || defaultSessionLength;
                      const pct = Math.min(100, Math.round((estTime / targetTime) * 100));
                      
                      return (
                        <>
                          <button
                            onClick={() => setTimePopupDay(i)}
                            className="relative w-full flex items-center gap-3 overflow-hidden rounded-2xl border border-white/[0.14] bg-white/[0.04] px-3 py-3 backdrop-blur-md active:scale-[0.97] transition-transform"
                            style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), 0 10px 26px rgba(0,0,0,0.45)' }}
                            data-testid={`time-trigger-${i}`}
                            aria-label="Adjust session time"
                          >
                            <img
                              src="/time-pill-photo.webp"
                              alt=""
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-y-0 right-0 h-full w-3/5 select-none object-cover opacity-60"
                              style={{ maskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)', WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)' }}
                              draggable={false}
                            />
                            <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/[0.10] bg-black/70">
                              <Clock size={22} className="text-white" />
                            </span>
                            <span className="relative min-w-0 flex-1 text-left">
                              <span className="block font-heading text-[20px] font-black uppercase leading-none tracking-tight text-white" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                                Time
                              </span>
                              <span className="mt-1 block truncate text-[12px] text-white/60" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
                                Adjust your workout time
                              </span>
                            </span>
                            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-black/70">
                              <ChevronDown size={18} className="text-white" />
                            </span>
                          </button>
                          
                          {/* Time adjustment popup */}
                          {timePopupDay === i && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center px-6" onClick={() => setTimePopupDay(null)}>
                              <div className="absolute inset-0 bg-black/60" />
                              <div className="relative z-10 w-full max-w-sm rounded-2xl overflow-hidden border border-white/[0.15] bg-[#0A0A0A]" style={{ boxShadow: '0 24px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)' }} onClick={e => e.stopPropagation()}>
                                <div className="p-5 pb-3">
                                  <h3 className="text-lg font-heading font-black uppercase tracking-tight text-white mb-1">Adjust Session Time</h3>
                                  <p className="text-xs text-[#A1A1AA] leading-relaxed">Short on time? Your coach will prioritise the most important exercises and trim the rest to fit your schedule.</p>
                                </div>
                                <div className="grid grid-cols-4 gap-2 px-5 pb-3">
                                  {[30, 45, 60, 75].map(mins => {
                                    const isActive = (day.session_length_override || defaultSessionLength) === mins;
                                    return (
                                      <button
                                        key={mins}
                                        data-testid={`time-override-${day.day_number}-${mins}`}
                                        onClick={async () => {
                                          try {
                                            const res = await api.put(`/workouts/day/${day.day_number}/override`, { session_length: mins });
                                            if (res.data?.success) {
                                              
                                              fetchWorkout();
                                              setTimePopupDay(null);
                                            }
                                          } catch (err) {
                                            toast.error('Could not adjust');
                                          }
                                        }}
                                        className={`py-3 rounded-xl text-base font-heading font-bold transition-all active:scale-95 ${
                                          isActive
                                            ? 'bg-accent text-accent-foreground shadow-[0_0_12px_rgba(var(--accent-rgb),0.2)]'
                                            : 'bg-white/[0.06] text-white border border-white/[0.1] hover:border-accent/30'
                                        }`}
                                      >
                                        {mins}m
                                      </button>
                                    );
                                  })}
                                </div>
                                {day.session_length_override && day.session_length_override !== defaultSessionLength && (
                                  <p className="text-[10px] text-accent/60 text-center pb-2 font-mono uppercase">Override active — exercises adjusted</p>
                                )}
                                <button onClick={() => setTimePopupDay(null)} className="w-full py-3 border-t border-white/10 text-sm font-heading font-bold uppercase tracking-wider text-[#A1A1AA] active:bg-white/5">
                                  Close
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}


                    {/* Cardio First toggle — only show if day has both warmup/exercises AND cardio */}
                    {day.cardio_block && (day.warmup?.length > 0 || day.main?.length > 0) && (
                      <button
                        onClick={() => setCardioFirstDays(p => ({ ...p, [i]: !p[i] }))}
                        className={`relative w-full flex items-center gap-3 overflow-hidden rounded-2xl border px-3 py-3 backdrop-blur-md active:scale-[0.97] transition-transform ${
                          cardioFirstDays[i] ? 'border-[#0A84FF]/45 bg-[#0A84FF]/[0.10]' : 'border-white/[0.14] bg-white/[0.04]'
                        }`}
                        style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), 0 10px 26px rgba(0,0,0,0.45)' }}
                        data-testid={`cardio-first-toggle-${i}`}
                        aria-label={cardioFirstDays[i] ? 'Cardio first on' : 'Do cardio first'}
                        aria-pressed={!!cardioFirstDays[i]}
                      >
                        <img
                          src="/cardio-pill-photo.webp"
                          alt=""
                          aria-hidden="true"
                          className={`pointer-events-none absolute inset-y-0 right-0 h-full w-3/5 select-none object-cover ${cardioFirstDays[i] ? 'opacity-35' : 'opacity-60'}`}
                          style={{ maskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)', WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 55%)' }}
                          draggable={false}
                        />
                        <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/[0.10] bg-black/70">
                          <Footprints size={22} className={cardioFirstDays[i] ? 'text-[#4FA9FF]' : 'text-white'} />
                        </span>
                        <span className="relative min-w-0 flex-1 text-left">
                          <span className="block font-heading text-[19px] font-black uppercase leading-none tracking-tight text-white" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                            Do Cardio First
                          </span>
                          <span className="mt-1 block truncate text-[12px] text-white/60" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
                            {cardioFirstDays[i] ? 'Cardio moved to the start' : 'Start with your cardio block'}
                          </span>
                        </span>
                        <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-black/70 ${cardioFirstDays[i] ? 'border-[#0A84FF]/50' : 'border-white/[0.12]'}`}>
                          {cardioFirstDays[i]
                            ? <Check size={18} className="text-[#4FA9FF]" />
                            : <ChevronRight size={18} className="text-white" />}
                        </span>
                      </button>
                    )}

                    {/* === CARDIO FIRST: Show cardio before warmup/exercises === */}
                    {cardioFirstDays[i] && day.cardio_block && (() => {
                      const cardioTimeKey = `cardio-${i}`;
                      const defaultCardioMins = day.cardio_block.duration_minutes || 15;
                      const currentCardioMins = localTimeOverrides[cardioTimeKey] ?? defaultCardioMins;
                      const sessionType = day.cardio_block.session_type || day.cardio_block.type || 'steady';
                      const typeLabel = (day.cardio_block.rounds_segments?.length > 1)
                        ? 'Rounds mix'
                        : sessionType.charAt(0).toUpperCase() + sessionType.slice(1).replace(/_/g, ' ');
                      
                      return (
                      <div>
                        <h4 className="text-base font-heading uppercase tracking-tight text-white font-black mb-3 flex items-center gap-2">
                          <Activity size={14} className="text-accent" /> 
                          Cardio First
                        </h4>
                        <div className="rounded-2xl bg-[#141414] border-2 border-white/[0.18] p-4" data-testid="cardio-first-card">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#1F1F1F] shrink-0 border-2 border-white/[0.18]">
                              {(day.cardio_block.image_url || getCardioImage(day.cardio_block.modality_name, day.cardio_block.modality_id)) ? (
                                <CardioMedia modalityId={day.cardio_block.modality_id} src={day.cardio_block.image_url || getCardioImage(day.cardio_block.modality_name, day.cardio_block.modality_id)} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center"><Activity size={24} className="text-accent" /></div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs tracking-wider uppercase text-accent font-heading font-bold">{typeLabel}</span>
                              <div className="font-heading text-2xl uppercase text-white tracking-tight leading-tight">{(day.cardio_block.rounds_segments?.length > 1 ? day.cardio_block.exercise : day.cardio_block.modality_name) || 'Cardio'}</div>
                            </div>
                            <button
                              onClick={() => openCardioReplace(i)}
                              className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-[#A1A1AA] active:scale-90 active:bg-accent active:text-accent-foreground transition-all shrink-0"
                              data-testid={`replace-cardio-first-${i}`}
                            >
                              <MoreVertical size={16} />
                            </button>
                          </div>
                          {day.cardio_block.rounds_protocol ? (
                            day.cardio_block.rounds_segments?.length > 1 ? null : (
                            <CardioRoundsStepper
                              cardioBlock={day.cardio_block}
                              planId={workout?.id}
                              dayNumber={day.day_number}
                              onUpdated={(block) => applyCardioBlockUpdate(i, block)}
                              testId={`cardio-first-rounds-stepper-${i}`}
                            />)
                          ) : (
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                        <span className="text-base text-white font-heading font-black uppercase tracking-tight">Duration</span>
                            <div className="flex items-center gap-2 bg-[#1F1F1F] rounded-full p-1 border border-white/10">
                              <button onClick={() => setLocalTimeOverrides(p => ({ ...p, [cardioTimeKey]: Math.max(1, currentCardioMins - 1) }))} className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"><Minus size={16} strokeWidth={2.5} /></button>
                              <span className="font-heading text-xl text-white w-10 text-center">{currentCardioMins}</span>
                              <button onClick={() => setLocalTimeOverrides(p => ({ ...p, [cardioTimeKey]: Math.min(60, currentCardioMins + 1) }))} className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"><Plus size={16} strokeWidth={2.5} /></button>
                            </div>
                            <span className="text-base text-white font-heading font-bold">min</span>
                          </div>
                          )}
                          <CardioPrescriptionDetails cardioBlock={day.cardio_block} />
                          {(day.cardio_block.user_override || day.cardio_block.has_user_override) && (
                            <p className="mt-2 text-xs text-amber-400/80 leading-relaxed" data-testid={`cardio-user-override-note-${i}`}>
                              Your pick — not our recommendation. Your level's rounds still apply.
                            </p>
                          )}
                          <CardioMixCard
                            cardioBlock={day.cardio_block}
                            onSwapSegment={(segIdx) => openRoundsSegmentSwap(i, segIdx)}
                            onKeepSingle={() => handleKeepSingle(i)}
                            testId={`cardio-mix-card-cardio-first-${i}`}
                          />
                          <CardioIntervalToggle
                            cardioBlock={day.cardio_block}
                            planId={workout?.id}
                            dayNumber={day.day_number}
                            onUpdated={(block) => applyCardioBlockUpdate(i, block)}
                            testId={`cardio-first-interval-toggle-${i}`}
                          />
                        </div>
                      </div>
                      );
                    })()}

                    {/* Warmup — hidden when cardio first is active */}
                    {!cardioFirstDays[i] && day.warmup?.length > 0 && (
                      <div>
                        <h4 className="text-base font-heading uppercase tracking-tight text-white font-black mb-3 flex items-center gap-2">
                          <Flame size={14} className="text-orange-400" /> Warm-up
                        </h4>
                        {day.warmup.map((ex, j) => {
                          const warmupKey = ex.id || `${dayKey}-warmup-${ex.exercise}-${j}`;
                          const isCardioWarmup = ex.is_cardio_warmup || ex.exercise_type === 'cardio_warmup';
                          const timeKey = `warmup-${i}-${j}`;
                          const defaultMins = parseInt(ex.duration) || 5;
                          const currentMins = localTimeOverrides[timeKey] ?? defaultMins;
                          
                          if (isCardioWarmup) {
                            return (
                              <div
                                key={warmupKey}
                                className="rounded-2xl bg-[#141414] border border-white/10 p-4 mb-2"
                                data-testid={`cardio-warmup-${j}`}
                              >
                                <div className="flex items-center gap-4">
                                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#1F1F1F] shrink-0 border border-white/10">
                                    {(ex.image_url || getCardioImage(ex.exercise)) ? (
                                      <CardioMedia modalityId={ex.modality_id} src={ex.image_url || getCardioImage(ex.exercise)} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center"><Activity size={24} className="text-accent" /></div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-xs tracking-wider uppercase text-accent font-heading font-bold">Cardio Warm-Up</span>
                                    <button onClick={() => setSelectedCardioWarmup(ex)} className="block">
                                      <span className="font-heading text-xl uppercase text-white tracking-tight leading-tight">{ex.exercise?.replace('Warmup Cardio: ', '') || 'Cardio Warmup'}</span>
                                    </button>
                                  </div>
                                </div>
                                {/* Time stepper */}
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                              <span className="text-base text-white font-heading font-black uppercase tracking-tight">Duration</span>
                                  <div className="flex items-center gap-2 bg-[#1F1F1F] rounded-full p-1 border border-white/10">
                                    <button
                                      onClick={() => setLocalTimeOverrides(p => ({ ...p, [timeKey]: Math.max(3, currentMins - 1) }))}
                                      className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
                                      data-testid={`warmup-decrease-${j}`}
                                    ><Minus size={16} strokeWidth={2.5} /></button>
                                    <span className="font-heading text-xl text-white w-10 text-center" data-testid={`warmup-time-${j}`}>{currentMins}</span>
                                    <button
                                      onClick={() => setLocalTimeOverrides(p => ({ ...p, [timeKey]: Math.min(30, currentMins + 1) }))}
                                      className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
                                      data-testid={`warmup-increase-${j}`}
                                    ><Plus size={16} strokeWidth={2.5} /></button>
                                  </div>
                                  <span className="text-base text-white font-heading font-bold">min</span>
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                          <div key={warmupKey} className="py-2.5 flex items-center justify-between border-b border-border/30 last:border-0">
                            <button
                              onClick={() => setSelectedExercise({ name: ex.exercise })}
                              className="text-base font-semibold hover:text-accent transition-colors text-left text-white"
                              data-testid={`warmup-exercise-${j}`}
                            >
                              {ex.exercise}
                            </button>
                            <span className="text-sm font-mono font-bold text-[#A1A1AA] shrink-0 ml-3">{ex.duration}</span>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {day.main?.length > 0 && (
                      <div>
                        <h4 className="text-base font-heading uppercase tracking-tight text-white font-black mb-3 flex items-center gap-2">
                          <Dumbbell size={14} className="text-accent" /> Main Exercises
                        </h4>
                        {day.main.map((ex, j) => {
                          const mainKey = ex.id || `${dayKey}-main-${ex.exercise}-${j}`;
                          const isUserAdded = ex.user_added === true;
                          // Local 3-5KB thumb first: the video-frame fallback made the first
                          // open of a day fetch a dozen cross-origin MP4s just to paint 64px.
                          const stillImg = ex.media_urls?.thumbnail || ex.images_male?.[0] || ex.images_female?.[0] || ex.media_urls?.images?.[0] || null;
                          const videoUrl = ex.media_urls?.video || ex.video_url || '';
                          return (
                          <div key={mainKey} className={`rounded-2xl overflow-hidden border border-white/[0.08] mb-3 bg-gradient-to-b from-white/[0.04] to-white/[0.01] ${isUserAdded ? 'border-l-4 border-l-accent' : ''}`} style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 24px rgba(0,0,0,0.35)' }}>
                            {/* Hero image */}
                            <div className="relative">
                            <div
                              onClick={() => setSelectedExercise({ name: ex.exercise, dayIndex: i, exerciseIndex: j })}
                              className="relative w-full h-40 overflow-hidden cursor-pointer rounded-2xl"
                              style={{ background: 'radial-gradient(120% 120% at 50% 0%, #161616 0%, #0A0A0A 70%)' }}
                              data-testid={`exercise-thumb-${j}`}
                            >
                              {stillImg ? (
                                <img src={stillImg} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                              ) : videoUrl && !isSlowConnection ? (
                                <video src={`${videoUrl}#t=0.1`} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                              ) : (
                                <div className="w-full h-full bg-[#1F1F1F] flex items-center justify-center">
                                  <Dumbbell size={32} className="text-white/20" />
                                </div>
                              )}
                              {/* Top edge highlight + bottom vignette (visual only) */}
                              <div className="absolute inset-x-0 top-0 h-px bg-white/[0.08] pointer-events-none" />
                              <div
                                className="absolute inset-x-0 bottom-0 h-12 pointer-events-none"
                                style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.85) 0%, transparent 100%)' }}
                              />
                              {/* Small play icon top-right */}
                              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                                <Play size={14} fill="white" className="text-white ml-0.5" />
                              </div>
                            </div>
                            {/* Sibling of the tappable image so flagging never opens the demo */}
                            <ExerciseFlagButton
                              exercise={ex.exercise}
                              primaryMuscle={ex.primary_muscle}
                              equipment={ex.equipment}
                              planId={workout?.id}
                              dayIndex={i}
                              source="weekly_view"
                              testId={`exercise-flag-weekly-${j}`}
                            />
                            </div>
                            
                            {/* Exercise name + muscle-group badge BELOW image */}
                            <div className="px-4 pt-3 pb-1">
                              <h3 className="font-heading text-base font-black uppercase tracking-tight text-white leading-tight" data-testid={`main-exercise-${j}`}>{ex.exercise}</h3>
                            </div>
                            
                            {/* Set count badge, then swap + replace */}
                            <div className="px-4 pt-2 pb-3 space-y-2">
                              <MuscleSetsBoxes
                                muscle={ex.primary_muscle}
                                sets={ex.sets}
                                reps={ex.reps}
                                muscleTestId={`muscle-badge-${j}`}
                                setsTestId={`sets-reps-badge-${j}`}
                              />
                              <div className="grid grid-cols-2 gap-2">
                                {isUserAdded ? (
                                  <button
                                    onClick={() => handleRemoveExercise(i, j, ex.exercise)}
                                    className="py-2.5 rounded-xl bg-red-500/15 border border-red-500/30 text-xs font-heading font-bold uppercase tracking-wider text-red-400 active:scale-[0.97] transition-transform flex items-center justify-center gap-1.5"
                                    data-testid={`remove-exercise-${j}`}
                                  >
                                    <Trash2 size={12} /> Remove
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => openPickerForReplace(i, j)}
                                    className="py-2.5 rounded-xl border-2 border-blue-500/30 text-white font-heading font-black uppercase tracking-[0.09em] text-xs active:scale-[0.97] transition-all flex items-center justify-center gap-1.5"
                                    style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 50%, #1D4ED8 100%)' }}
                                    data-testid={`replace-exercise-${j}`}
                                    aria-label="Replace exercise"
                                  >
                                    <RefreshCw size={13} strokeWidth={2.5} /> Replace
                                  </button>
                                )}
                                {!isUserAdded && (
                                  <button
                                    onClick={() => { setQuickSwapDayEx({ dayIndex: i, exerciseIndex: j, exercise: ex }); loadQuickSwapForWeekly(ex, i, j); }}
                                    className="py-2.5 rounded-xl border-2 border-orange-500/30 text-white font-heading font-black uppercase tracking-[0.09em] text-xs active:scale-[0.97] transition-all flex items-center justify-center gap-1.5"
                                    style={{ background: 'linear-gradient(135deg, #7C2D12 0%, #EA580C 50%, #9A3412 100%)' }}
                                    data-testid={`quick-swap-weekly-${j}`}
                                    aria-label="Swap muscle group"
                                  >
                                    <Repeat size={13} strokeWidth={2.5} /> Swap
                                  </button>
                                )}
                              </div>
                            </div>
                            {ex.progression && (
                              <div className="px-4 pb-3">
                                {ex.progression.action === 'calibration' ? (
                                  <span
                                    className="inline-flex items-center gap-2 rounded-full border-2 border-accent/40 px-3 py-1.5 font-heading text-[11px] font-black uppercase tracking-[0.18em] text-accent"
                                    style={{
                                      background: 'linear-gradient(135deg, rgba(var(--accent-rgb),0.22) 0%, rgba(var(--accent-rgb),0.07) 100%)',
                                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14), 0 4px 16px rgba(var(--accent-rgb),0.22)',
                                      textShadow: '0 0 10px rgba(var(--accent-rgb),0.5)',
                                    }}
                                    data-testid={`calibration-banner-${j}`}
                                  >
                                    <span
                                      className="h-2 w-2 shrink-0 rounded-full bg-accent"
                                      style={{ boxShadow: '0 0 8px 2px rgba(var(--accent-rgb),0.65)' }}
                                      aria-hidden="true"
                                    />
                                    Calibrating
                                  </span>
                                ) : (
                                  <ProgressionBadge progression={ex.progression} compact />
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })}
                        {/* Add Exercise button */}
                        <button
                          onClick={() => openPicker(i, day.type, day.main)}
                          className="w-full mt-2 py-2.5 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700/60 text-muted-foreground text-xs font-medium flex items-center justify-center gap-1.5 hover:border-lime-400 dark:hover:border-accent/30 hover:text-lime-600 dark:hover:text-accent/70 hover:bg-lime-50/50 dark:hover:bg-accent-deep transition-all active:scale-[0.98]"
                          data-testid={`add-exercise-day-${i}`}
                        >
                          <Plus size={14} /> Add Exercise
                        </button>
                      </div>
                    )}

                    {/* Add Exercise for rest days or days with no main exercises */}
                    {(!day.main || day.main.length === 0) && day.type && !day.type.toLowerCase().includes('rest') && (
                      <div>
                        <button
                          onClick={() => openPicker(i, day.type, day.main || [])}
                          className="w-full py-2.5 rounded-xl border border-dashed border-zinc-700/60 text-muted-foreground text-xs font-medium flex items-center justify-center gap-1.5 hover:border-accent/30 hover:text-accent/70 hover:bg-accent-deep transition-all active:scale-[0.98]"
                          data-testid={`add-exercise-day-${i}`}
                        >
                          <Plus size={14} /> Add Exercise
                        </button>
                      </div>
                    )}

                    {/* Cardio Block — only show here if NOT cardio-first */}
                    {!cardioFirstDays[i] && day.cardio_block && (() => {
                      const cardioTimeKey = `cardio-${i}`;
                      const defaultCardioMins = day.cardio_block.duration_minutes || 15;
                      const currentCardioMins = localTimeOverrides[cardioTimeKey] ?? defaultCardioMins;
                      const sessionType = day.cardio_block.session_type || day.cardio_block.type || 'steady';
                      const typeLabel = (day.cardio_block.rounds_segments?.length > 1)
                        ? 'Rounds mix'
                        : sessionType.charAt(0).toUpperCase() + sessionType.slice(1).replace(/_/g, ' ');
                      
                      return (
                      <div className="mt-4 pt-4 border-t border-border">
                        <h4 className="text-base font-heading uppercase tracking-tight text-white font-black mb-3 flex items-center gap-2">
                          <Activity size={14} className="text-accent" /> 
                          {day.cardio_is_main ? 'Cardio Session' : 'Cardio Finisher'}
                        </h4>
                        <div className="rounded-2xl bg-[#141414] border border-white/10 p-4" data-testid="cardio-finisher-card">
                          <div className="flex items-start gap-4">
                            <div className="shrink-0">
                              <div className="w-24 h-24 rounded-xl overflow-hidden bg-[#1F1F1F] border border-white/10">
                                {(day.cardio_block.image_url || getCardioImage(day.cardio_block.modality_name, day.cardio_block.modality_id)) ? (
                                  <CardioMedia modalityId={day.cardio_block.modality_id} src={day.cardio_block.image_url || getCardioImage(day.cardio_block.modality_name, day.cardio_block.modality_id)} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center"><Activity size={28} className="text-accent" /></div>
                                )}
                              </div>
                              <button
                                onClick={() => openCardioReplace(i)}
                                className="mt-2 w-24 h-8 rounded-full border border-accent/35 bg-accent/10 text-accent flex items-center justify-center gap-1 text-[10px] font-heading font-black uppercase tracking-wider active:scale-95 active:bg-accent active:text-accent-foreground transition-all"
                                data-testid={`replace-cardio-${i}`}
                                aria-label="Change cardio exercise"
                              >
                                <RefreshCw size={11} strokeWidth={2.5} /> Change
                              </button>
                            </div>
                            <div className="flex-1 min-w-0 pt-0.5">
                              <span className="text-xs tracking-wider uppercase text-accent font-heading font-bold">{typeLabel}</span>
                              <div className="font-heading text-2xl uppercase text-white tracking-tight leading-tight">{(day.cardio_block.rounds_segments?.length > 1 ? day.cardio_block.exercise : day.cardio_block.modality_name) || 'Cardio'}</div>
                            </div>
                          </div>
                          {/* Time stepper — rounds blocks step sets instead of minutes */}
                          {day.cardio_block.rounds_protocol ? (
                            day.cardio_block.rounds_segments?.length > 1 ? null : (
                            <CardioRoundsStepper
                              cardioBlock={day.cardio_block}
                              planId={workout?.id}
                              dayNumber={day.day_number}
                              onUpdated={(block) => applyCardioBlockUpdate(i, block)}
                              testId={`cardio-rounds-stepper-${i}`}
                            />)
                          ) : (
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                        <span className="text-base text-white font-heading font-black uppercase tracking-tight">Duration</span>
                            <div className="flex items-center gap-2 bg-[#1F1F1F] rounded-full p-1 border border-white/10">
                              <button
                                onClick={() => setLocalTimeOverrides(p => ({ ...p, [cardioTimeKey]: Math.max(1, currentCardioMins - 1) }))}
                                className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
                                data-testid="cardio-decrease-time"
                              ><Minus size={16} strokeWidth={2.5} /></button>
                              <span className="font-heading text-xl text-white w-10 text-center" data-testid="cardio-time-display">{currentCardioMins}</span>
                              <button
                                onClick={() => setLocalTimeOverrides(p => ({ ...p, [cardioTimeKey]: Math.min(60, currentCardioMins + 1) }))}
                                className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
                                data-testid="cardio-increase-time"
                              ><Plus size={16} strokeWidth={2.5} /></button>
                            </div>
                            <span className="text-base text-white font-heading font-bold">min</span>
                          </div>
                          )}
                          <CardioPrescriptionDetails cardioBlock={day.cardio_block} />
                          {(day.cardio_block.user_override || day.cardio_block.has_user_override) && (
                            <p className="mt-2 text-xs text-amber-400/80 leading-relaxed" data-testid={`cardio-user-override-note-${i}`}>
                              Your pick — not our recommendation. Your level's rounds still apply.
                            </p>
                          )}
                          <CardioMixCard
                            cardioBlock={day.cardio_block}
                            onSwapSegment={(segIdx) => openRoundsSegmentSwap(i, segIdx)}
                            onKeepSingle={() => handleKeepSingle(i)}
                            testId={`cardio-mix-card-finisher-${i}`}
                          />
                          <CardioIntervalToggle
                            cardioBlock={day.cardio_block}
                            planId={workout?.id}
                            dayNumber={day.day_number}
                            onUpdated={(block) => applyCardioBlockUpdate(i, block)}
                            testId={`cardio-finisher-interval-toggle-${i}`}
                          />
                        </div>
                      </div>
                      );
                    })()}

                    {/* Add Cardio placeholder — shown in expanded view when day has no cardio block */}
                    {!cardioFirstDays[i] && !day.cardio_block && day.main?.length > 0 && !isLockedDay && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <h4 className="text-base font-heading uppercase tracking-tight text-white/40 font-black mb-3 flex items-center gap-2">
                          <Activity size={14} className="text-white/30" />
                          Cardio Finisher
                        </h4>
                        <button
                          onClick={() => openCardioReplace(i)}
                          className="w-full rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-4 flex items-center gap-4 hover:border-accent/30 hover:bg-accent/[0.03] active:scale-[0.99] transition-all group"
                          data-testid={`add-cardio-expanded-${i}`}
                        >
                          <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-accent/20 transition-all">
                            <Plus size={22} className="text-white/25 group-hover:text-accent/60 transition-colors" />
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-xs font-heading font-bold uppercase tracking-wider text-white/30 group-hover:text-accent/60 transition-colors">Add Cardio Finisher</div>
                            <div className="text-sm text-white/20 mt-0.5">Tap to select a cardio modality</div>
                          </div>
                        </button>
                      </div>
                    )}

                    {day.cooldown?.length > 0 && (
                      <div>
                        <button
                          onClick={() => setCooldownExpanded(p => ({ ...p, [i]: !p[i] }))}
                          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-xs font-heading font-bold uppercase tracking-wider active:scale-[0.98] transition-all ${
                            cooldownExpanded[i]
                              ? 'bg-white/5 text-white border border-white/[0.15]'
                              : 'bg-white/5 text-white border border-white/[0.08]'
                          }`}
                          data-testid={`cooldown-toggle-${i}`}
                        >
                          <span className="flex items-center gap-2">
                            <Heart size={14} className="text-accent" />
                            Cool-Down · {day.cooldown.length} stretches
                          </span>
                          {cooldownExpanded[i] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {cooldownExpanded[i] && (
                          <div className="mt-3 space-y-2">
                            {day.cooldown.map((ex, j) => {
                              const cooldownKey = ex.id || `${dayKey}-cooldown-${ex.exercise}-${j}`;
                              const stillImg = ex.media_urls?.thumbnail || ex.images_male?.[0] || ex.images_female?.[0] || ex.media_urls?.images?.[0] || null;
                              const videoUrl = ex.media_urls?.video || ex.video_url || '';
                              return (
                              <div key={cooldownKey} className="rounded-xl bg-[#141414] border border-white/10 p-3 flex items-center gap-3">
                                <ExerciseThumb
                                  img={stillImg}
                                  videoUrl={videoUrl}
                                  onClick={() => setSelectedExercise({ name: ex.exercise })}
                                  testId={`cooldown-thumb-${j}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <button
                                    onClick={() => setSelectedExercise({ name: ex.exercise })}
                                    className="font-heading text-base uppercase text-white tracking-tight leading-tight text-left w-full"
                                    data-testid={`cooldown-exercise-${j}`}
                                  >
                                    {ex.exercise}
                                  </button>
                                </div>
                                <span className="text-xs font-mono font-bold text-[#A1A1AA] shrink-0">{ex.duration}</span>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="px-1">{dayActionButton}</div>
                  </motion.div>
                )}

                {/* Same rest check as the card styling above: regenerated/padded plans mark rest days with type 'Rest' only (no is_rest). */}
                {(!day.main || day.main.length === 0) && (day.type === 'Rest' || day.type === 'Recovery' || day.is_rest) && (
                  <div className="px-5 pb-5">
                    <div className="rounded-2xl border border-accent/15 p-3" style={{ background: 'linear-gradient(135deg, rgba(var(--accent-rgb),0.03) 0%, rgba(0,0,0,0) 100%)' }}>
                      <RecoveryTiles log={recoveryLog} date={weekDates[i]} testId={`rest-day-recovery-blocks-${i + 1}`} />
                    </div>
                  </div>
                )}

                {expandedDay === i && (!day.main || day.main.length === 0) && !day.cardio_block && !day.is_rest && day.session_type && (
                  <div className="px-4 pb-4 text-center py-6">
                    <p className="text-sm text-muted-foreground mb-3">Exercises couldn't be loaded for this session.</p>
                    <button
                      onClick={handleRegeneratePlan}
                      disabled={regenerating}
                      className="px-4 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                      data-testid="regen-empty-plan-btn"
                    >
                      {regenerating ? 'Rebuilding...' : 'Rebuild Program'}
                    </button>
                  </div>
                )}
              </motion.div>
              {i < (workout.days?.length || 0) - 1 && (
                <div className="md:hidden mx-10 h-[2px] rounded-full bg-accent/80" aria-hidden="true" data-testid={`day-divider-${i + 1}`} />
              )}
              </Fragment>
              );
            });
            })()}
          </div>
        </motion.div>
      </main>

      {/* Exercise Detail Modal */}
      {selectedExercise && (
        <ExerciseDetailModal
          exerciseName={typeof selectedExercise === 'string' ? selectedExercise : selectedExercise.name}
          onClose={() => setSelectedExercise(null)}
          workoutPlanId={workout?.id}
          dayIndex={typeof selectedExercise === 'object' ? selectedExercise.dayIndex : undefined}
          exerciseIndex={typeof selectedExercise === 'object' ? selectedExercise.exerciseIndex : undefined}
          onExerciseReplaced={({ updatedDay, dayIndex, newExercise, exerciseIndex }) => {
            try {
              // Update the local workout state with replaced exercise
              setWorkout(prev => {
                if (!prev) return prev;
                const newDays = [...prev.days];
                if (updatedDay && dayIndex !== undefined) {
                  newDays[dayIndex] = updatedDay;
                } else if (newExercise && dayIndex !== undefined && exerciseIndex !== undefined) {
                  const mainKey = newDays[dayIndex]?.main ? 'main' : 'exercises';
                  const main = [...(newDays[dayIndex]?.[mainKey] || [])];
                  main[exerciseIndex] = newExercise;
                  newDays[dayIndex] = { ...newDays[dayIndex], [mainKey]: main };
                }
                return { ...prev, days: newDays };
              });
            } catch (err) {
              console.error('[Workout] Replace update error:', err);
            }
            setSelectedExercise(null); // Close modal
          }}
        />
      )}

      {/* Cardio Warmup Detail Modal */}
      {selectedCardioWarmup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedCardioWarmup(null)}
          data-testid="cardio-warmup-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="cardio-warmup-modal"
          >
            {/* Header with close button */}
            <div className="sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  <Activity size={20} className="text-foreground" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold" data-testid="cardio-warmup-name">
                    {selectedCardioWarmup.exercise?.replace('Warmup Cardio: ', '') || 'Cardio Warmup'}
                  </h2>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{selectedCardioWarmup.duration}</span>
                    <span>•</span>
                    <span>RPE {selectedCardioWarmup.rpe}</span>
                    <span>•</span>
                    <span className="text-orange-400">Easy Intensity</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setSelectedCardioWarmup(null)} 
                className="p-2 rounded-full hover:bg-muted transition-colors"
                data-testid="cardio-warmup-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Image */}
            {selectedCardioWarmup.image_url && (
              <div className="relative bg-gradient-to-b from-zinc-900 to-black">
                <div className="relative w-full aspect-[3/2] overflow-hidden">
                  <CardioMedia
                    modalityId={selectedCardioWarmup.modality_id}
                    src={selectedCardioWarmup.image_url}
                    alt={selectedCardioWarmup.exercise?.replace('Warmup Cardio: ', '')}
                    className="w-full h-full object-cover"
                    videoClassName="w-full h-full object-contain"
                    style={{ objectPosition: 'top center' }}
                    eager
                  />
                  {/* BodyMax Watermark */}
                  <div className="absolute bottom-3 right-3 pointer-events-none">
                    <span 
                      className="font-bold text-sm tracking-wide"
                      style={{ 
                        background: 'linear-gradient(135deg, var(--accent-hex) 0%, #ffffff 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        textShadow: '0 2px 10px rgba(var(--accent-rgb), 0.3)'
                      }}
                    >
                      BodyMax
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-5 space-y-5">
              {/* Warmup Purpose */}
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <p className="text-sm text-muted-foreground">
                  {selectedCardioWarmup.notes || 'Light cardio to gradually elevate your heart rate and prepare your body for the workout ahead.'}
                </p>
              </div>

              {/* Instructions */}
              {selectedCardioWarmup.instructions?.length > 0 && (
                <div>
                  <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
                    <Dumbbell size={16} className="text-accent" />
                    How to Perform
                  </h3>
                  <ol className="space-y-2">
                    {selectedCardioWarmup.instructions.map((step, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-mono">
                          {i + 1}
                        </span>
                        <span className="text-muted-foreground leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Coaching Cues */}
              {selectedCardioWarmup.coaching_cues?.length > 0 && (
                <div>
                  <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2 text-green-500">
                    <CheckCircle size={16} /> Coaching Cues
                  </h3>
                  <ul className="space-y-1.5">
                    {selectedCardioWarmup.coaching_cues.map((cue, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <CheckCircle size={12} className="text-green-500 shrink-0 mt-1" />
                        <span className="text-muted-foreground">{cue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Info */}
              <div className="flex items-center gap-4 pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 text-sm">
                  <Clock size={14} className="text-muted-foreground" />
                  <span>{selectedCardioWarmup.duration}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <Flame size={14} className="text-orange-400" />
                  <span>RPE ≤ {selectedCardioWarmup.rpe}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <Heart size={14} className="text-red-400" />
                  <span>Low Intensity</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}


      {/* Exercise Picker */}
      <ExercisePicker
        isOpen={pickerState.open}
        onClose={closePicker}
        workoutPlanId={workout?.id}
        dayIndex={pickerState.dayIndex}
        dayType={pickerState.dayType}
        existingExercises={pickerState.exercises}
        mode={pickerState.mode}
        replaceIndex={pickerState.replaceIndex}
        onExerciseAdded={handleExerciseAdded}
        onExerciseReplaced={handleExerciseReplaced}
      />

      {/* Demo Session Popup */}
      {showDemoPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6 py-6 overflow-y-auto" onClick={() => { setShowDemoPopup(false); setDemoDayInfo(null); }}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 w-full max-w-sm my-auto rounded-3xl overflow-hidden border border-[#0165FE]/35 bg-[#07090C]" style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.7), 0 0 46px rgba(1,101,254,0.2), inset 0 1px 0 rgba(255,255,255,0.07)' }} onClick={e => e.stopPropagation()}>
            {/* Video hero — same clip as the landing page (1.6 MB, poster paints instantly) */}
            <div className="relative w-full aspect-video overflow-hidden bg-black">
              <img src="/welcome-video-hdr-poster.webp" alt="" fetchpriority="high"
                   className="absolute inset-0 w-full h-full object-cover object-top" />
              <video
                data-testid="demo-popup-video"
                poster="/welcome-video-hdr-poster.webp"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover object-top"
              >
                <source src="/welcome-video-hdr.mp4" type="video/mp4" />
                <source src="/welcome-video-hdr.webm" type="video/webm" />
              </video>
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, #07090C 3%, rgba(7,9,12,0.55) 42%, transparent 78%)' }} />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <span className="inline-block px-2.5 py-1 rounded-full font-heading text-[10px] font-black uppercase tracking-[0.2em] text-white"
                      style={{ background: 'rgba(1,101,254,0.92)', boxShadow: '0 4px 14px rgba(1,101,254,0.45)' }}>
                  Free demo
                </span>
                <h3 className="mt-2 text-[22px] leading-[1.05] font-heading font-black uppercase tracking-tight text-white">Please Start Your Demo</h3>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-[13.5px] text-white/65 leading-relaxed">
                This is a <strong className="font-bold text-[#4D9BFF]">free demo workout</strong> designed just for you. Complete it to experience how BODYMAX personalises your training.
              </p>
              <div className="space-y-2">
                {[[Dumbbell, 'Full workout with real exercises'], [Activity, 'Track your sets, reps & weight'], [Brain, 'Your coach learns your strength level']].map(([Icon, text]) => (
                  <div key={text} className="flex items-center gap-3 p-2.5 rounded-xl border border-white/[0.07]"
                       style={{ background: 'linear-gradient(100deg, rgba(1,101,254,0.10) 0%, rgba(255,255,255,0.02) 60%)' }}>
                    <span className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: 'rgba(1,101,254,0.18)', border: '1px solid rgba(1,101,254,0.35)' }}>
                      <Icon size={15} className="text-[#4D9BFF]" />
                    </span>
                    <span className="text-[13px] text-white/75 leading-snug">{text}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => { setShowDemoPopup(false); if (demoDayInfo) navigate(`/workout/active/${demoDayInfo.planId}/${demoDayInfo.dayNumber}${demoDayInfo.cardioFirst ? '?cardioFirst=1' : ''}`); }}
                className="w-full py-3.5 rounded-xl text-white font-heading font-black uppercase active:scale-[0.97] transition-all flex flex-col items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#0165FE 0%,#0148C4 100%)', boxShadow: '0 10px 28px rgba(1,101,254,0.42)' }}
                data-testid="start-demo-btn"
              >
                <BeginSessionLabel />
              </button>
            </div>
            <button
              onClick={() => { setShowDemoPopup(false); setDemoDayInfo(null); navigate('/subscribe'); }}
              className="w-full py-3 border-t border-white/10 text-sm font-heading font-bold uppercase tracking-wider text-[#4D9BFF] active:bg-white/5"
            >
              Subscribe to Unlock All Days
            </button>
          </div>
        </div>
      )}

      {/* Quick Swap Sheet (Weekly View) */}
      {showQuickSwapSheet && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" onClick={() => { setShowQuickSwapSheet(false); setQuickSwapDayEx(null); }}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 bg-[#0A0A0A] border-t border-accent/30 rounded-t-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 120px)' }}>
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 rounded-full bg-white/20" /></div>
            <div className="px-5 pt-2 pb-4">
              <h3 className="text-2xl font-heading font-black uppercase tracking-tight text-white">Smart Swap</h3>
              <p className="text-sm text-white/50 mt-1">Coach-picked alternatives for <span className="text-white font-heading font-bold uppercase">{quickSwapDayEx?.exercise?.exercise}</span></p>
            </div>
            {quickSwapLoading ? (
              <div className="flex items-center justify-center py-10 gap-2">
                <Loader2 size={20} className="animate-spin" style={{ color: '#FF6B00' }} />
                <span className="text-sm text-white/50 font-heading uppercase tracking-wider">Finding alternatives...</span>
              </div>
            ) : quickSwapOptions.length === 0 ? (
              <p className="text-center py-10 text-sm text-white/40">No alternatives found</p>
            ) : (
              <div className="space-y-3 px-5">
                {quickSwapOptions.map((option, idx) => {
                  const thumbUrl = option.media_urls?.thumbnail || null;
                  const videoUrl = option.media_urls?.video || option.video_url || '';
                  return (
                  <button
                    key={option.name}
                    onClick={() => confirmQuickSwapWeekly(option)}
                    className="w-full text-left rounded-2xl border-2 border-orange-500/30 active:scale-[0.97] transition-all overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, #7C2D12 0%, #EA580C 50%, #9A3412 100%)' }}
                  >
                    <div className="flex gap-3 p-4">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-black/30">
                        {thumbUrl ? (
                          <img src={thumbUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : videoUrl && !isSlowConnection ? (
                          <video src={`${videoUrl}#t=0.1`} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Dumbbell size={20} className="text-orange-300/40" /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-base uppercase tracking-tight text-white leading-tight">{option.name}</div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="px-2.5 py-1 rounded-lg bg-black/30 text-xs font-heading font-bold uppercase tracking-wider text-orange-200">{option.equipment?.replace('_', ' ')}</span>
                          <span className="px-2.5 py-1 rounded-lg bg-black/30 text-xs font-heading font-bold uppercase tracking-wider text-orange-200">{option.primary_muscle?.replace('_', ' ')}</span>
                        </div>
                        {option.match_reason && <div className="text-sm text-orange-100/80 mt-1.5 font-medium">{option.match_reason}</div>}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cardio Replacement Sheet */}
      <CardioRiskSheet
        open={!!riskPrompt}
        modality={riskPrompt?.mod}
        advisory={riskPrompt?.advisory}
        accepting={acceptingRisk}
        onCancel={() => setRiskPrompt(null)}
        onAccept={acceptCardioRisk}
      />


      {replaceCardioDayIdx !== null && (
        <div className="fixed inset-0 z-50 flex flex-col" data-testid="cardio-replace-sheet">
          <div className="flex-1 bg-black/60" onClick={closeCardioSheet} />
          <div ref={cardioSheetRef} className="bg-[#0A0A0A] border-t border-white/10 rounded-t-2xl max-h-[80vh] overflow-y-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
            <div className="px-5 pt-5 pb-3 flex items-center justify-between sticky top-0 bg-[#0A0A0A] z-10">
              <h3 className="text-xl font-heading font-black uppercase tracking-tight text-white" data-testid="cardio-sheet-title">
                {roundsSwap
                  ? `Swap movement ${roundsSwap.segmentIndex + 1} of ${roundsSwap.total}`
                  : (workout?.days?.[replaceCardioDayIdx]?.cardio_block ? 'Replace Cardio' : 'Add Cardio')}
              </h3>
              <button onClick={closeCardioSheet} className="w-8 h-8 rounded-full bg-[#1F1F1F] flex items-center justify-center active:scale-90">
                <X size={16} className="text-[#A1A1AA]" />
              </button>
            </div>
            {loadingCardioMods ? (
              <div className="flex items-center justify-center py-10 gap-2">
                <Loader2 size={20} className="animate-spin text-accent" />
                <span className="text-sm text-[#A1A1AA] font-heading uppercase tracking-wider">Loading...</span>
              </div>
            ) : (
              <div className="px-5 pb-2 space-y-6">
                {roundsSwap && (
                  <p className="text-sm text-white/50" data-testid="cardio-sheet-rounds-note">
                    This slot keeps its <span className="text-accent font-mono">{roundsSwap.minutes} min</span> — pick the movement you want.
                    Star the ones you love and your coach will build future mixes from those.
                  </p>
                )}
                {/* Session Type */}
                {!roundsSwap && (
                <div>
                  <div className="text-sm font-heading font-bold uppercase tracking-wider text-accent mb-3 flex items-center gap-2">
                    <Zap size={14} /> Session Type
                  </div>
                  <CardioTypeCards value={cardioSessionType} onChange={setCardioSessionType} />
                </div>
                )}

                {/* Duration stepper */}
                {!roundsSwap && (
                <div>
                  <div className="text-sm font-heading font-bold uppercase tracking-wider text-[#A1A1AA] mb-3">Duration</div>
                  <div className="flex items-center justify-center gap-3">
                    <div className="flex items-center gap-2 bg-[#1F1F1F] rounded-full p-1 border border-white/10">
                      <button
                        onClick={() => setCardioDuration(prev => Math.max(1, prev - 1))}
                        data-testid="cardio-duration-minus"
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
                      ><Minus size={16} strokeWidth={2.5} /></button>
                      <span className="font-heading text-2xl text-white w-12 text-center" data-testid="cardio-duration-value">{cardioDuration}</span>
                      <button
                        onClick={() => setCardioDuration(prev => Math.min(60, prev + 1))}
                        data-testid="cardio-duration-plus"
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
                      ><Plus size={16} strokeWidth={2.5} /></button>
                    </div>
                    <span className="text-sm text-[#A1A1AA] font-mono">min</span>
                  </div>
                </div>
                )}

                {!roundsSwap && (
                  <AutoMixToggle value={autoMix} onChange={setAutoMix} testId="cardio-auto-mix-toggle" />
                )}

                {/* Modality grid */}
                <div>
                  <div className="text-sm font-heading font-bold uppercase tracking-wider text-[#A1A1AA] mb-3">
                    {roundsSwap ? 'Choose Movement' : 'Choose Modality'}
                  </div>
                  {!roundsSwap && cardioSessionType === 'rounds' && (
                    <p className="text-xs text-white/45 mb-3" data-testid="cardio-rounds-only-note">
                      Showing the movements your audit sets up as rounds.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {(!roundsSwap && cardioSessionType === 'rounds'
                      ? cardioModalities.filter((m) => m.rounds_capable)
                      : cardioModalities).map((mod, idx) => {
                      const locked = mod.eligible === false;
                      return (
                      <button
                        key={mod.id || idx}
                        onClick={() => pickCardioModality(mod)}
                        disabled={locked && !mod.override_allowed}
                        className={`group overflow-hidden rounded-2xl text-left transition-transform bg-[#141414] border ${locked ? (mod.override_allowed ? 'opacity-70 active:scale-[0.97] border-white/[0.07]' : 'opacity-45 cursor-not-allowed border-white/[0.07]') : 'active:scale-[0.97]'} ${mod.id === currentCardioModalityId ? 'border-accent ring-1 ring-accent/50' : 'border-white/[0.07]'}`}
                        data-testid={`cardio-mod-${idx}`}
                        aria-current={mod.id === currentCardioModalityId ? 'true' : undefined}
                      >
                        <div className="relative w-full aspect-[4/3] overflow-hidden bg-black">
                          <CardioMedia modalityId={mod.id} src={mod.image_url || getCardioImage(mod.name, mod.id)} className={`w-full h-full object-cover transition-transform duration-300 ${locked ? 'grayscale' : 'group-hover:scale-105'}`} />
                          {locked && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/55">
                              <Lock size={20} className="text-white/70" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div className="font-heading font-black text-base uppercase tracking-tight text-white leading-tight">{mod.name}</div>
                          {mod.id === currentCardioModalityId && !locked && (
                            <span className="mt-1.5 inline-block px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-[10px] font-heading font-black uppercase tracking-wider" data-testid={`cardio-mod-current-${idx}`}>Current</span>
                          )}
                          {locked ? (
                            <span
                              className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono font-bold ${mod.override_allowed ? 'bg-amber-400/10 border-amber-400/30 text-amber-400/90' : 'bg-white/[0.06] border-white/10 text-white/50'}`}
                              data-testid={`cardio-mod-locked-${idx}`}
                            >
                              <Lock size={10} />
                              {mod.locked_label || 'Locked'}
                              {mod.override_allowed ? ' · tap' : ''}
                            </span>
                          ) : mod.acknowledged ? (
                            <span
                              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-400/90 text-[11px] font-mono font-bold"
                              data-testid={`cardio-mod-unlocked-${idx}`}
                            >
                              <Unlock size={10} />
                              Unlocked by you
                            </span>
                          ) : roundsSwap ? (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); toggleCardioFavourite(mod.id); }}
                              className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono font-bold transition-colors ${cardioFavourites.includes(mod.id) ? 'bg-accent/15 border-accent/40 text-accent' : 'bg-white/[0.04] border-white/10 text-white/45'}`}
                              data-testid={`cardio-mod-favourite-${idx}`}
                            >
                              <Star size={10} fill={cardioFavourites.includes(mod.id) ? 'currentColor' : 'none'} />
                              Favourite
                            </span>
                          ) : (
                            <CardioDurationPill
                              duration={cardioDuration}
                              sessionType={cardioSessionType}
                              testId={`cardio-mod-duration-${idx}`}
                            />
                          )}
                        </div>
                      </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {/* Remove Cardio — only shown when an existing block is present */}
            {workout?.days?.[replaceCardioDayIdx]?.cardio_block && (
              <div className="px-5 pb-5 pt-1">
                <div className="h-px bg-white/[0.07] mb-4" />
                {removeCardioConfirm ? (
                  <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4">
                    <p className="text-sm text-white/70 text-center mb-3 font-heading">Remove this cardio finisher?</p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setRemoveCardioConfirm(false)}
                        className="flex-1 py-3 rounded-xl border border-white/15 text-white/50 text-sm font-heading font-bold uppercase tracking-wider active:scale-[0.97] transition-all"
                        data-testid="remove-cardio-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleCardioRemove(replaceCardioDayIdx)}
                        className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-400 text-sm font-heading font-bold uppercase tracking-wider active:scale-[0.97] transition-all"
                        data-testid="remove-cardio-confirm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setRemoveCardioConfirm(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/10 text-white/30 hover:text-red-400 hover:border-red-500/20 active:scale-[0.98] transition-all text-sm font-heading font-bold uppercase tracking-wider"
                    data-testid="remove-cardio-btn"
                  >
                    <Trash2 size={14} />
                    Remove Cardio Finisher
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
